import {
  Component,
  inject,
  OnDestroy,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormGroupDirective,
  Validators,
} from '@angular/forms';
import { v4 as uuidv4 } from 'uuid';
import dataDocuments from '../../../assets/data/documentsCarnet.json';
import {
  firstValueFrom,
  forkJoin,
  map,
  merge,
  Observable,
  Subscription,
} from 'rxjs';
import { debounceTime, retry, switchMap } from 'rxjs/operators';
import { MatStepper, StepperOrientation } from '@angular/material/stepper';
import { BreakpointObserver } from '@angular/cdk/layout';
import { CuestionarioSaludCardComponent } from '../cuestionario-salud-card/cuestionario-salud-card.component';
import {
  ContratoInfo,
  IntegranteContrato,
  SubtipoNovedad,
  TipoNovedad,
  TIPO_NOVEDAD_STORAGE_KEY,
} from '../tipo-novedad-selector/tipo-novedad-selector.component';
import { TipoNovedadSelectorBridgeService } from '../../services/radicacion-traslados-service/tipo-novedad-selector-bridge.service';
import { LocalStorageService } from '../../local-storage/local-storage.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ModalBasicComponent } from '../common/dialogs/modal-basic/modal-basic.component';
import { TableNovedadTrasladosComponent } from '../table-novedad-traslados/table-novedad-traslados.component';
import { TiposDocumentoService } from '../../services/tipos-documento/tipos-documento.service';
import { ContratosUsersService } from '../../services/radicacion-traslados-service/contratos-users.service';
import { ValidacionContratoService } from '../../services/radicacion-traslados-service/validacion-contrato.service';
import { SubtiposNovedadService } from '../../services/radicacion-traslados-service/subtipos-novedad.service';
import { TipoSolicitudNovedadTrasladoIdService } from '../../services/radicacion-traslados-service/tipo-solicitud-novedad-traslado-id.service';
import { ContratoAfilmedDTO } from '../../models/contrato-afilmed/contrato-afilmed.model';
import { SoporteDocumentosCardComponent } from '../soporte-documentos-card/soporte-documentos-card.component';
import { FiltroCuestionarioSalud } from '../../models/filtro-cuestionario-salud/filtro-cuestionario-salud';
import { PerfilamientoService } from '../../services/perfilamiento/perfilamiento.service';
import {
  IntegrantePerfilamientoDTO,
  PerfilamientoRequest,
} from '../../models/perfilamiento/perfilamiento-request';
import { PerfilamientoResponse } from '../../models/perfilamiento/perfilamiento-response';
import { InterfazAuditoriaService } from '../../services/interfaz-auditoria/interfaz-auditoria.service';
import { InterfazAuditoriaResponse } from '../../models/interfaz-auditoria/interfaz-auditoria-response';
import { InterfazAuditoriaRequest } from '../../models/interfaz-auditoria/interfaz-auditoria-request';
import { SoporteDocumentalRequest } from '../../models/soporte-documental/soporte-documental-request';
import { SoporteDocumentalService } from '../../services/soporte-documental/soporte-documental.service';
import { SoporteIntegranteDTO } from '../../models/soporte-documental/soporte-documental-response';
import { CuestionarioSaludSnapshotService } from '../../services/cuestionario-salud-snapshot/cuestionario-salud-snapshot.service';
import { CuestionarioSnapshotRequest } from '../../models/cuestionario-snapshot/cuestionario-snapshot';
import { PreguntasSarlaftComponent } from '../preguntas-sarlaft/preguntas-sarlaft.component';
import { AutorizacionRadicacionComponent } from '../autorizacion-radicacion/autorizacion-radicacion.component';
import { RadicarSolicitudService } from '../../services/radicar-solicitud/radicar-solicitud.service';
import {
  DatosTipoNovedadRequest,
  RadicarSolicitudRequest,
} from '../../models/radicar-solicitud/radicar-solicitud-request';
import { IntegranteSolicitudService } from '../../services/integrante-solicitud/integrante-solicitud.service';
import { IntegranteSolicitudRequest } from '../../models/integrante-solicitud/integrante-solicitud';
import { ProgressStep } from '../../models.progress-step/progress-step';
import {
  DocumentoFormatoDTO,
  DocumentoGeneradoSolicitudDTO,
  GeneracionDocumentosTrasladosService,
  SeleccionDocumentosRequest,
  ValidarFirmaSolicitudResponseDTO,
} from '../../services/radicacion-traslados-service/generacion-documentos-traslados.service';
import { environment as ENV } from '../../../environments/environment';
import {
  FirmaService,
  EnlaceFirmaResponse,
} from '../../services/firma/firma.service';
import { FileServerService } from '../../services/file-server/file-server.service';
import { ColectivoEspecialService } from '../../services/radicacion-traslados-service/colectivo-especial.service';
import { MaincontainerComponent } from '../maincontainer/maincontainer.component';
import { InfoContratoUsuarioDTO } from '../../models/info-contrato-usuario/info-contrato-usuario.model';
import { ValidarSolicitudDuplicadaService } from '../../services/validar-solicitud-duplicada/validar-solicitud-duplicada.service';

@Component({
  selector: 'app-radicacion-traslados',
  templateUrl: './radicacion-traslados.component.html',
  styleUrls: ['./radicacion-traslados.component.css'],
})
export class RadicacionTrasladosComponent implements OnDestroy {
  /**
   * Identificador técnico del contrato origen para el POST de radicación.
   * Depende de qué exponga Afilmed/contratos-user en cada entorno.
   */
  private resolveIdContratoOrigen(): number | string | null {
    const keys = [
      'idContratoOrigen',
      'ID_CONTRATO_ORIGEN',
      'IdContratoOrigen',
      'idContrato',
      'IdContrato',
      'ID_CONTRATO',
    ];
    const sources: Record<string, any>[] = [
      this.currentContract as any,
      this.filaContratoAfilmedPaso1 as any,
    ].filter(Boolean);

    for (const src of sources) {
      const raw = this.pickFirstDefined(src, keys, '');
      if (raw === '' || raw === null || raw === undefined) continue;
      if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
      const s = String(raw).trim();
      if (!s) continue;
      if (/^\d+$/.test(s)) return Number(s);
      return s;
    }
    return null;
  }

  /** Solo desarrollo: ids enviados en radicar para cruzar con BD. */
  private logContratoOrigenParaRevisionBd(body: RadicarSolicitudRequest): void {
    if (ENV.production) return;
    const co = body.contratoOrigen;
    const id = co?.idContratoOrigen ?? co?.ID_CONTRATO_ORIGEN ?? null;
    if (id == null || id === '') {
      const keys = [
        'idContratoOrigen',
        'ID_CONTRATO_ORIGEN',
        'IdContratoOrigen',
        'idContrato',
        'IdContrato',
        'ID_CONTRATO',
      ];
      const snapshot: Record<string, unknown> = {};
      for (const src of [
        this.currentContract as unknown as Record<string, unknown>,
        this.filaContratoAfilmedPaso1 as unknown as Record<
          string,
          unknown
        > | null,
      ].filter(Boolean) as Record<string, unknown>[]) {
        for (const k of keys) {
          const v = src[k];
          if (v !== undefined && v !== null && String(v).trim() !== '') {
            snapshot[k] = v;
          }
        }
      }
      console.warn(
        '[radicar] Sin PK en el body. Campos de id en currentContract / fila tabla:',
        snapshot,
      );
    }
  }

  private pickFirstDefined(
    source: Record<string, any>,
    keys: string[],
    fallback = '',
  ): any {
    for (const key of keys) {
      const value = source[key];
      if (
        value !== null &&
        value !== undefined &&
        String(value).trim() !== ''
      ) {
        return value;
      }
    }
    return fallback;
  }

  public frmConsult: FormGroup = new FormGroup({});
  documentTypeData: any[] = dataDocuments as Array<any>;
  selectedContractUsers: any[] = [];
  stepperOrientation: Observable<StepperOrientation>;
  items: {
    title: string;
    template: TemplateRef<any>;
    collapsed: boolean;
    disabled: boolean;
    readonly: boolean;
  }[] = [];
  private maxStepIndexAlcanzado = 0;
  hasSelectionNovedadTraslados = false;
  isSubmitting = false;
  auditDecision: 'si' | 'no' | null = null;
  @ViewChild('tmplVerContratos') tmplVerContratos!: TemplateRef<any>;
  @ViewChild('tmplVerDetallesContrato')
  tmplVerDetallesContrato!: TemplateRef<any>;
  @ViewChild('tmplSeleccionarTipoNovedad')
  tmplSeleccionarTipoNovedad!: TemplateRef<any>;
  @ViewChild('tmplRealizarPerfilamientoNovedad')
  tmplRealizarPerfilamientoNovedad!: TemplateRef<any>;
  @ViewChild('tmplValidarAuditoria') tmplValidarAuditoria!: TemplateRef<any>;
  @ViewChild('tmplRadicarSolicitud')
  tmplRadicarSolicitud!: TemplateRef<any>;
  @ViewChild('stepper') stepper!: MatStepper;
  @ViewChild(FormGroupDirective) formGroupDirective!: FormGroupDirective;

  private readonly camposTipoNovedadCriticos = [
    'tipoNovedad',
    'selectedTipoNovedad',
    'idTipoSolicitud',
    'tipoPlanDestino',
    'programaDestino',
    'tarifaDestino',
    'corteDestino',
    'subcuentaDestino',
    'sucursalDestino',
    'colectivo',
    'programaDestinoColectivo',
    'tarifaDestinoColectivo',
    'corteSubcuenta',
    'proporcionalidadColectivo',
    'tipoPlanDestinoContrato',
    'programaDestinoContrato',
    'subcuentaDestinoContrato',
    'corteDestinoContrato',
    'tarifaDestinoContrato',
    'sucursalComercializableContrato',
  ] as const;
  @ViewChild('progressContent', { static: true })
  progressContent!: TemplateRef<any>;
  @ViewChild('contratoGenerado', { static: true })
  contratoGenerado!: TemplateRef<any>;
  contratoSeleccionado: ContratoInfo | null = null;
  /** Fila bruta de `contratos-users` del contrato abierto con el ojo (evita depender solo de localStorage en el hijo). */
  filaContratoAfilmedPaso1: ContratoAfilmedDTO | null = null;
  integrantesSeleccionados: IntegranteContrato[] = [];
  integrantesSeleccionadosPaso1: IntegranteContrato[] = [];
  tipoNovedadSeleccionada: TipoNovedad | null = null;
  subtipoNovedadSeleccionada: SubtipoNovedad | null = null;
  subtipoNovedadSeleccionadaNombre: string | null = null;
  validacionTipoNovedadCompleta = false;
  tooltipPlacement: 'end' | 'bottom' = 'end';
  selectedContract: string = '';
  currentContract!: ContratoAfilmedDTO;
  @ViewChildren(CuestionarioSaludCardComponent)
  healthQuestCards!: QueryList<CuestionarioSaludCardComponent>;
  @ViewChildren(SoporteDocumentosCardComponent)
  soporteDocumentosCards!: QueryList<SoporteDocumentosCardComponent>;
  isAllHealthQuestsValid = false;
  @ViewChild(TableNovedadTrasladosComponent)
  tableTraslados!: TableNovedadTrasladosComponent;
  tiposDocumento: any[] = [];
  sarlaftValido = false;
  sarlaftCoincidenciaValida = true;
  autorizacionValida = false;
  contratosData: ContratoAfilmedDTO[] = [];
  isLoadingContratos = false;
  validacionTablaData: any[] = [];
  isLoadingValidacion = false;
  errorLoadingContrato = false;
  private lastIdContract = '';
  private colectivoEspecialRequestId = 0;
  private consultFormPersistSub?: Subscription;
  soportesCompletos: boolean[] = [];
  modalAbiertoCuestionarioSalud = false;
  mostrarCuestionariosSalud = true;
  mostrarTablaCumplePerfilamiento = true;
  puedeAvanzarPerfilamiento = false;
  isLoadingCuestionario = false;
  isLoadingAuditoria = false;
  isLoadingSoportes = false;
  soportesDocumentales: SoporteIntegranteDTO[] = [];
  // Subtipos catalog
  private allSubtiposCatalog: any[] = [];
  public disponibleSubtipos: string[] = [];
  modalRef!: NgbModalRef;
  private idSolicitudTemporal: string = '';
  private idTipoSolicitudTraslado = 0;
  /** Capturado al radicar; evita perder el id si localStorage se degradó en pasos posteriores. */
  private idTipoSolicitudCapturadoParaDocumentos = 0;
  private integrantesSolicitudGuardados: Array<{
    id: number;
    nombreUsuario: string;
    nroDocumento: string;
  }> = [];
  public solicitudRadicadaId: number | null = null;
  /** Último fallo HTTP al consultar documentos generados (reintentos); no confundir con lista vacía 200. */
  private ultimoErrorDocumentosGenerados: string | null = null;
  /** Último fallo HTTP de `validar-firma` cuando la petición lanza. */
  private ultimoErrorValidarFirma: string | null = null;
  public documentosGeneradosSolicitud: DocumentoGeneradoSolicitudDTO[] = [];
  public validacionFirmaSolicitud: ValidarFirmaSolicitudResponseDTO | null =
    null;
  public enlaceFirmaGenerado: EnlaceFirmaResponse | null = null;
  public errorEnlaceFirma: string | null = null;
  public pasosGeneracionDocumentos: ProgressStep[] = [
    { label: 'Radicando solicitud', status: 'completed' },
    {
      label: 'Generando documentos',
      status: 'in-progress',
    },
    { label: 'Validando documentos para firma', status: 'pending' },
  ];
  isRadicando = false;
  attrDataPerfilamiento: string[] = [
    'No.',
    'Identificacion',
    'NombreUsuario',
    'CampaniaVigente',
    'Congelamiento',
    'Anexo',
    'asociado',
    'epsInactivo',
    'regimen',
  ];

  namesColumnsPerfilamiento: string[] = [
    'No.',
    'Identificación',
    'Nombre Usuario',
    'Campaña Cont Origen.',
    'Congelamiento',
    'Anexo',
    'Asociado',
    'Eps Inactivo',
    'Regimen',
  ];

  attrDataNoPerfilamiento: string[] = [
    'No.',
    'Identificacion',
    'NombreUsuario',
    'mora',
    'licencia',
    'edadIngreso',
    'Parentesco',
    'acuerdoPago',
    'CampaniaVigente',
    'Congelamiento',
    'Anexo',
    'asociado',
    'epsInactivo',
    'regimen',
  ];

  namesColumnsNoPerfilamiento: string[] = [
    'No.',
    'Identificación',
    'Nombre Usuario',
    'Mora',
    'Licencia',
    'Edad ingreso',
    'Parentesco',
    'Acuerdo de pago',
    'Campaña Cont. Origen',
    'Congelamiento',
    'Anexo',
    'Asociado',
    'Eps Inactivo',
    'Regimen',
  ];

  attrDataComPerfilamiento: string[] = [
    'No.',
    'Identificacion',
    'NombreUsuario',
  ];

  namesColumnsComPerfilamiento: string[] = [
    'No.',
    'Identificación',
    'Nombre Usuario',
  ];

  infoContratoUsuarios: InfoContratoUsuarioDTO[] = [];
  cumplePerfilamiento: any[] = [];
  noCumplePerfilamiento: any[] = [];
  cuestionarioSaludUsuarios: FiltroCuestionarioSalud[] = [];
  resultadosInterfazAuditoria: InterfazAuditoriaResponse[] = [];
  @ViewChildren('cardRef')
  cuestionarioCards!: QueryList<CuestionarioSaludCardComponent>;
  @ViewChild(PreguntasSarlaftComponent)
  preguntasSarlaft!: PreguntasSarlaftComponent;
  @ViewChild(AutorizacionRadicacionComponent)
  autorizacionRadicacion!: AutorizacionRadicacionComponent;
  private readonly tituloDetalleContratoBase =
    'Consultar usuarios - Ver detalle del contrato';
  private numeroContratoTituloDetalle = '';
  sarlaftColapsado = false;
  autorizacionColapsado = false;
  soportesColapsado = false;

  constructor(
    private mainCmp: MaincontainerComponent,
    private _formBuilder: FormBuilder,
    private localStorageService: LocalStorageService,
    private modalService: NgbModal,
    private tiposDocumentoService: TiposDocumentoService,
    private contratosUsersService: ContratosUsersService,
    private validacionContratoService: ValidacionContratoService,
    private subtiposNovedadService: SubtiposNovedadService,
    private tipoSolicitudNovedadTrasladoIdService: TipoSolicitudNovedadTrasladoIdService,
    private perfilamientoService: PerfilamientoService,
    private interfazAuditoriaService: InterfazAuditoriaService,
    private soporteDocumentalService: SoporteDocumentalService,
    private cuestionarioSaludSnapshotService: CuestionarioSaludSnapshotService,
    private radicarSolicitudService: RadicarSolicitudService,
    private validarDuplicadoService: ValidarSolicitudDuplicadaService,
    private integrantesService: IntegranteSolicitudService,
    private generacionDocumentosService: GeneracionDocumentosTrasladosService,
    private firmaService: FirmaService,
    private fileServerService: FileServerService,
    private colectivoEspecialService: ColectivoEspecialService,
    private tipoNovedadSelectorBridge: TipoNovedadSelectorBridgeService,
  ) {
    this.buildForm();
    const breakpointObserver = inject(BreakpointObserver);
    this.stepperOrientation = breakpointObserver
      .observe('(min-width: 768px)')
      .pipe(map(({ matches }) => (matches ? 'horizontal' : 'vertical')));
  }

  ngOnInit(): void {
    this.updateTooltipPlacement();
    window.addEventListener('resize', () => this.updateTooltipPlacement());

    this.restoreEstadoProceso();

    this.tiposDocumentoService.getTiposDocumento().subscribe({
      next: (data) => (this.tiposDocumento = data),
    });

    this.loadCatalogoSubtipos();
  }

  ngOnDestroy(): void {
    this.consultFormPersistSub?.unsubscribe();
  }

  private loadCatalogoSubtipos(): void {
    this.allSubtiposCatalog = [];
    this.tipoSolicitudNovedadTrasladoIdService
      .getIdTipoSolicitudNovedadTraslado()
      .pipe(
        // Solo para consultar catálogo de subtipos: no pisar idTipoSolicitudTraslado,
        // que debe reflejar el tipo de solicitud resuelto al elegir el tipo de novedad.
        switchMap((idTipoSolicitud) =>
          this.subtiposNovedadService.getSubtiposNovedad({
            idTipoSolicitud,
            contratoTransitorio: false,
          }),
        ),
      )
      .subscribe({
        next: (dtos: any[]) => {
          try {
            this.allSubtiposCatalog = dtos || [];
            this.disponibleSubtipos = this.allSubtiposCatalog.flatMap(
              (t: any) =>
                (t.subtipos || [])
                  .filter((s: any) => s && (s.estado === 1 || s.estado === '1'))
                  .map((s: any) => String(s.subtipoNovedad)),
            );
            this.disponibleSubtipos = Array.from(
              new Set(this.disponibleSubtipos),
            );
          } catch (err) {
            this.allSubtiposCatalog = [];
            this.disponibleSubtipos = [];
          }
        },
        error: (err) => {
          this.allSubtiposCatalog = [];
          this.disponibleSubtipos = [];
        },
      });
  }

  private loadInfoContrato(
    contrato: ContratoAfilmedDTO,
    usuariosContrato: any[],
  ) {
    const integrantes: IntegrantePerfilamientoDTO[] = usuariosContrato.map(
      (u) => ({
        'No.': u.no,
        Identificacion: u.identificacion,
        NombreUsuario: u.nombreUsuario,
        Parentesco: u.parentesco,
        Edad: u.edad,
        Genero: u.genero,
        FechaNacimiento: u.fechaNacimiento,
        FechaAntiguedad: u.fechaAntiguedad,
        Anexo: u.anexo ?? null,
        ValorTarifaAnexo: u.valorTarifaAnexo,
        ValorTarifaPrograma: u.valorTarifaPrograma,
        TotalTarifa: u.totalTarifa,
        CampaniaVigente: u.campaniaVigente || null,
        Congelamiento: u.congelamiento,
        Tarifa: u.Tarifa,
        EstructuraTarifaria: u.EstructuraTarifaria,
      }),
    );

    const state = this.getTipoDeNovedadLocalStorage();

    const request: PerfilamientoRequest = {
      contratoOrigen: contrato.contrato,
      contratoDestino: state.contratoDestino?.codigo ?? null,
      tipoNovedadId: state?.selectedTipoNovedad?.codigo ?? null,
      subtipoNovedadId: state?.selectedSubtipoNovedad?.id ?? null,
      lineaNegocio: contrato.LineaNegocio,
      ...this.buildDestinoPerfilamiento(state),
      integrantes,
    };

    this.isLoadingCuestionario = true;

    this.perfilamientoService.evaluar(request).subscribe({
      next: (response: PerfilamientoResponse) => {
        this.procesarResultadoPerfilamiento(response, contrato);
        // Ninguno cumple perfilamiento
        if (response.totalCumplen === 0) {
          this.mostrarCuestionariosSalud = false;
          this.mostrarTablaCumplePerfilamiento = false;
          this.puedeAvanzarPerfilamiento = false;
          this.isLoadingCuestionario = false;
          this.abrirModalInfoCuestionarioSalud(response.mensajeAdvertencia!);
        } else {
          this.mostrarCuestionariosSalud = true;
          this.mostrarTablaCumplePerfilamiento = true;
          this.puedeAvanzarPerfilamiento = true;
          this.isLoadingCuestionario = false;
          this.abrirModalInfoCuestionarioSalud(response.mensajeExito);
        }
        this.saveToLocalStorage(3);
      },
      error: (err) => {
        console.error('Error ejecutando evaluación de perfilamiento', err);

        this.abrirModalInfoCuestionarioSalud(
          'Ha ocurrido un error al ejecutar la evaluación de perfilamiento. Por favor intente nuevamente. Si el problema persiste, contacte al administrador del sistema.',
        );
        this.isLoadingCuestionario = false;
      },
    });
  }

  private buildDestinoPerfilamiento(
    state: any,
  ): Pick<
    PerfilamientoRequest,
    'grupoPrograma' | 'programaDestino' | 'tipoPlanDestino'
  > {
    const tipo = state?.selectedTipoNovedad?.codigoTipoNovedad;
    const subtipo = state?.selectedSubtipoNovedad?.codigoSubtipo;
    const data = {
      grupoPrograma: null,
      programaDestino: null,
      tipoPlanDestino: null,
    };

    if (tipo === 'TRASLADO_CONTRATO_EXISTENTE') {
      return {
        tipoPlanDestino: state.tipoPlanDestinoExistente ?? null,
        grupoPrograma: state.grupoProgramaDestinoExistente ?? null,
        programaDestino: state.programaDestinoExistente ?? null,
      };
    }

    if (tipo === 'TRASLADO_CONTRATO_NUEVO' || tipo === 'CAMBIO_PLAN') {
      return {
        tipoPlanDestino: state.tipoPlanDestinoContrato?.codigo ?? null,
        grupoPrograma: state.programaDestinoContrato?.grupoPrograma ?? null,
        programaDestino: state.programaDestinoContrato?.codigo ?? null,
      };
    }

    if (tipo === 'CAMBIO_COBERTURA') {
      if (
        ['TRASLADO_DOWNGRADE', 'TRASLADO_UPGRADE', 'TRASLADO_LINEAL'].includes(
          subtipo,
        )
      ) {
        return {
          tipoPlanDestino: state.tipoPlanDestino?.codigo ?? null,
          grupoPrograma: state.programaDestino?.grupoPrograma,
          programaDestino: state.programaDestino?.codigo ?? null,
        };
      }

      if (
        ['TRASLADO_COLECTIVO', 'CAMBIO_PERSONA_NATURAL_A_JURIDICO'].includes(
          subtipo,
        )
      ) {
        return {
          tipoPlanDestino: state.tipoPlanDestino?.codigo ?? null,
          grupoPrograma: state.programaDestinoColectivo?.grupoPrograma ?? null,
          programaDestino: state.programaDestinoColectivo?.codigo ?? null,
        };
      }
    }

    return data;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.mainCmp.setTitle('RADICACIÓN DE TRASLADOS');
    });
    Promise.resolve().then(async () => {
      this.initializeItems();
      try {
        const data = await firstValueFrom(
          this.tiposDocumentoService.getTiposDocumento(),
        );
        this.tiposDocumento = Array.isArray(data) ? data : [];
      } catch {
        this.tiposDocumento = [];
      }
      this.loadLocalStorage();
      this.setupConsultFormPersistence();
      this.setupHealthQuestValidation();

      this.healthQuestCards.changes.subscribe(() => {
        this.setupHealthQuestValidation();
      });
    });
  }

  /** Guarda tipo/número de documento y contrato al editar (sin depender solo de «Consultar»). */
  private setupConsultFormPersistence(): void {
    this.consultFormPersistSub?.unsubscribe();
    this.consultFormPersistSub = this.frmConsult.valueChanges
      .pipe(debounceTime(400))
      .subscribe(() => {
        const step =
          this.stepper?.selectedIndex != null
            ? this.stepper.selectedIndex + 1
            : 1;
        this.saveToLocalStorage(step);
      });
  }

  initializeItems() {
    this.items = [
      {
        title: 'Consultar usuarios - Ver contratos',
        template: this.tmplVerContratos,
        collapsed: true,
        disabled: true,
        readonly: false,
      },
      {
        title: 'Consultar usuarios - Ver detalle del contrato',
        template: this.tmplVerDetallesContrato,
        collapsed: true,
        disabled: true,
        readonly: false,
      },
      {
        title: 'Seleccionar tipo de novedad',
        template: this.tmplSeleccionarTipoNovedad,
        collapsed: true,
        disabled: true,
        readonly: false,
      },
      {
        title: 'Realizar perfilamiento de la novedad',
        template: this.tmplRealizarPerfilamientoNovedad,
        collapsed: true,
        disabled: true,
        readonly: false,
      },
      {
        title: 'Validar auditoría',
        template: this.tmplValidarAuditoria,
        collapsed: true,
        disabled: true,
        readonly: false,
      },
      {
        title: 'Radicar solicitud',
        template: this.tmplRadicarSolicitud,
        collapsed: true,
        disabled: true,
        readonly: false,
      },
    ];
  }

  onSelectionChange(hasRowsSelected: boolean) {
    this.hasSelectionNovedadTraslados = hasRowsSelected;
  }

  hasError(control: AbstractControl | null): boolean {
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    // Permitir operaciones de portapapeles y atajos comunes (Ctrl+V, Ctrl+C, Ctrl+X, etc.).
    // El flujo actual bloquea cualquier tecla no numérica vía `keydown`,
    // y con eso impide copiar/pegar.
    if (event.ctrlKey || event.metaKey) {
      return;
    }
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
    ];
    if (allowedKeys.includes(event.key)) {
      return;
    }
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  private buildForm(): void {
    this.frmConsult = this._formBuilder.group({
      documentType: [''],
      documentNumber: [
        '',
        [
          Validators.pattern('^[0-9]*$'),
          Validators.maxLength(30),
          Validators.minLength(5),
        ],
      ],
      contractNumber: ['', [Validators.pattern('^[0-9]*$')]],
    });
    this.applyConditionalValidators();
    this.updateContractNumberValidator();
  }

  private applyConditionalValidators(): void {
    this.frmConsult.get('documentNumber')!.valueChanges.subscribe((value) => {
      this.updateContractNumberValidator();

      const docTypeCtrl = this.frmConsult.get('documentType')!;
      if (value && !docTypeCtrl.value) {
        docTypeCtrl.markAsTouched();
        docTypeCtrl.markAsDirty();
        docTypeCtrl.updateValueAndValidity({ emitEvent: false });
      }
    });

    this.frmConsult.get('documentType')!.valueChanges.subscribe((value) => {
      this.updateContractNumberValidator();

      const docNumCtrl = this.frmConsult.get('documentNumber')!;
      if (value && !docNumCtrl.value) {
        docNumCtrl.markAsTouched();
        docNumCtrl.markAsDirty();
        docNumCtrl.updateValueAndValidity({ emitEvent: false });
      }
    });

    this.frmConsult.get('contractNumber')!.valueChanges.subscribe(() => {
      this.updateContractNumberValidator();
    });
  }

  private updateContractNumberValidator(): void {
    const docTypeCtrl = this.frmConsult.get('documentType')!;
    const docNumCtrl = this.frmConsult.get('documentNumber')!;
    const contractCtrl = this.frmConsult.get('contractNumber')!;

    const docTypeVal = (docTypeCtrl.value || '').toString().trim();
    const docNumVal = (docNumCtrl.value || '').toString().trim();
    const contractVal = (contractCtrl.value || '').toString().trim();

    const tieneDocumento = docTypeVal.length > 0 || docNumVal.length > 0;

    if (tieneDocumento) {
      docTypeCtrl.setValidators([Validators.required]);

      docNumCtrl.setValidators([
        Validators.required,
        Validators.pattern('^[0-9]*$'),
        Validators.maxLength(30),
        Validators.minLength(5),
      ]);

      contractCtrl.setValidators([Validators.pattern('^[0-9]*$')]);
    } else {
      docTypeCtrl.setValidators([]);

      docNumCtrl.setValidators([
        Validators.pattern('^[0-9]*$'),
        Validators.maxLength(30),
        Validators.minLength(5),
      ]);

      contractCtrl.setValidators([
        Validators.required,
        Validators.pattern('^[0-9]*$'),
      ]);
    }

    docTypeCtrl.updateValueAndValidity({ emitEvent: false });
    docNumCtrl.updateValueAndValidity({ emitEvent: false });
    contractCtrl.updateValueAndValidity({ emitEvent: false });

    this.frmConsult.updateValueAndValidity({ emitEvent: false });
  }

  onRowsSelected(rows: any[]) {
    this.selectedContractUsers = rows;
    this.integrantesSeleccionados = Array.isArray(rows) ? [...rows] : [];
    this.integrantesSeleccionadosPaso1 = [...rows];
    this.saveToLocalStorage(1);
  }

  get getControl(): { [key: string]: AbstractControl } {
    return {
      documentType: this.frmConsult.get('documentType')!,
      documentNumber: this.frmConsult.get('documentNumber')!,
      contractNumber: this.frmConsult.get('contractNumber')!,
    };
  }

  disableArrowKeys(event: KeyboardEvent) {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
    }
  }

  onSubmit() {
    if (this.frmConsult.valid) {
      this.frmConsult.disable();
      this.isSubmitting = true;
      this.isLoadingContratos = true;

      const { documentType, documentNumber, contractNumber } =
        this.frmConsult.getRawValue();

      const tieneDocumentoCompleto =
        !!documentType?.trim() && !!documentNumber?.trim();

      const params = {
        prm_docu_tipo: tieneDocumentoCompleto ? documentType : '',
        prm_docu_nro: tieneDocumentoCompleto ? documentNumber : '',
        prm_contra: contractNumber || '',
      };

      this.contratosUsersService.getContratosUsers(params).subscribe({
        next: (data) => {
          const result = Array.isArray(data) ? data : [];
          const contratoNoExiste =
            result.length === 0 ||
            !result[0]?.contrato ||
            result[0].contrato.trim() === '';

          if (contratoNoExiste) {
            this.contratosData = [];
            this.isLoadingContratos = false;
            this.isSubmitting = false;
            this.frmConsult.enable();
            this.mostrarModalNoExisteContrato(
              documentType,
              documentNumber,
              contractNumber,
            );
            return;
          }

          this.contratosData = Array.isArray(data) ? data : [];
          this.filaContratoAfilmedPaso1 = null;
          this.isLoadingContratos = false;
          this.setActiveStep(0);
          this.saveToLocalStorage(0);
        },
        error: (err) => {
          this.isLoadingContratos = false;
          this.frmConsult.enable();
          this.isSubmitting = false;
        },
      });
    } else {
      this.frmConsult.markAllAsTouched();
    }
  }

  private mostrarModalNoExisteContrato(
    documentType: string,
    documentNumber: string,
    contractNumber: string,
  ) {
    let mensaje: string;

    if (contractNumber) {
      mensaje = `No encontró contrato por el número: <strong>${contractNumber}</strong>`;
    } else {
      mensaje = `No se encontraron resultados coincidentes por el tipo de identificación: <strong>${documentType}</strong> y número de identificación: <strong>${documentNumber}</strong>`;
    }

    const modalRef = this.modalService.open(ModalBasicComponent, {
      centered: true,
      windowClass: 'custom-modal-radius',
    });

    modalRef.componentInstance.title = 'Sin resultados coincidentes';
    modalRef.componentInstance.content = mensaje;

    modalRef.componentInstance.buttons = [
      {
        text: 'Cerrar',
        color: 'btn-primary',
        minWidth: 'none',
        action: 'close',
        value: true,
      },
    ];
  }

  onReset() {
    if (!this.hayEstadoParaLimpiar()) {
      return;
    }

    const modalRef = this.modalService.open(ModalBasicComponent, {
      centered: true,
      windowClass: 'custom-modal-radius',
    });

    modalRef.componentInstance.title = 'Confirmación';
    modalRef.componentInstance.content = `¿Desea limpiar todos los campos del formulario? Esta acción eliminará los filtros aplicados y restablecerá el formulario a su estado inicial.`;

    modalRef.componentInstance.buttons = [
      {
        text: 'No',
        color: 'btn-secondary',
        minWidth: 'none',
        action: 'close',
        value: true,
      },
      {
        text: 'Sí',
        color: 'btn-primary',
        minWidth: 'none',
        action: 'custom',
        value: true,
        callback: () => {
          modalRef.close();
          this.performFullReset();
        },
      },
    ];
  }

  hayEstadoParaLimpiar(): boolean {
    const hasConsultValues =
      !!this.frmConsult.get('documentType')?.value ||
      !!this.frmConsult.get('documentNumber')?.value ||
      !!this.frmConsult.get('contractNumber')?.value;

    return !!(
      hasConsultValues ||
      this.localStorageService.load('frmConsultState') ||
      this.localStorageService.load(TIPO_NOVEDAD_STORAGE_KEY)
    );
  }

  /**
   * Reutilizable para limpiar formularios y storage sin confirmar (ej: al radicar en anulado).
   */
  resetAfterAnulacion(): void {
    this.performFullReset();
  }

  private performFullReset(): void {
    this.resetItems();

    this.isSubmitting = false;

    this.tipoNovedadSelectorBridge.resetState();

    this.formGroupDirective.resetForm();

    this.frmConsult.enable();

    this.stepper.reset();
    const nHq = this.healthQuestCards?.length ?? 0;
    const nSp = this.soporteDocumentosCards?.length ?? 0;
    this.localStorageService.clearRadicacionTrasladosSession(
      Math.max(32, nHq + 4),
      Math.max(32, nSp + 4),
    );

    this.isAllHealthQuestsValid = false;

    this.sarlaftValido = false;
    this.autorizacionValida = false;
    this.soportesCompletos = [];

    this.selectedContractUsers = [];
    this.integrantesSeleccionados = [];
    this.integrantesSeleccionadosPaso1 = [];
    this.tipoNovedadSeleccionada = null;
    this.subtipoNovedadSeleccionada = null;
    this.subtipoNovedadSeleccionadaNombre = null;
    this.validacionTipoNovedadCompleta = false;
    this.selectedContract = '';
    this.lastIdContract = '';
    this.contratoSeleccionado = null;
    this.currentContract = null as unknown as ContratoAfilmedDTO;
    this.contratosData = [];
    this.filaContratoAfilmedPaso1 = null;
    this.validacionTablaData = [];
    this.tableTraslados?.clearSelection();
    this.actualizarTituloDetalleContrato('');
    this.idSolicitudTemporal = '';
  }

  retryLoadContractDetails(): void {
    if (this.lastIdContract) {
      this.onViewContractDetails(this.currentContract);
    }
  }

  onViewContractDetails(contrato: ContratoAfilmedDTO) {
    void this.ejecutarSeleccionContratoPaso1(contrato);
  }

  private async ejecutarSeleccionContratoPaso1(
    contrato: ContratoAfilmedDTO,
  ): Promise<void> {
    const numeroSeleccionado = String(
      contrato.contrato ?? (contrato as any).Contrato ?? '',
    ).trim();
    this.selectedContract = numeroSeleccionado;
    this.lastIdContract = numeroSeleccionado;
    this.validacionTablaData = [];
    this.isLoadingValidacion = true;
    this.errorLoadingContrato = false;

    const contratoDetalle =
      this.obtenerFilaContratosUsers(numeroSeleccionado) ?? contrato;

    this.currentContract = { ...contratoDetalle };
    this.filaContratoAfilmedPaso1 = { ...contratoDetalle };
    this.actualizarTituloDetalleContrato(this.currentContract.contrato);

    this.contratoSeleccionado = {
      numero: contratoDetalle.contrato,
      estado: contratoDetalle.EstadoContrato,
      titular: contratoDetalle.Contratante,
      fechaInicio: new Date(),
      ProgramaCodigo: contratoDetalle.ProgramaCodigo,
      ProgramaNombre: contratoDetalle.ProgramaNombre,
      GrupoPrograma: contratoDetalle.GrupoPrograma,
      LineaNegocio: contratoDetalle.LineaNegocio,
      TipoPrima: contratoDetalle.TipoPrima,
      ColectivoEspecial: contratoDetalle.ColectivoEspecial,
      integrantes: this.contratoSeleccionado?.integrantes,
    };

    this.setActiveStep(1);

    await this.resolverColectivoEspecialContrato(
      contratoDetalle,
      numeroSeleccionado,
    );

    this.saveToLocalStorage(1);

    this.validacionContratoService
      .getInfoContratoUser(this.lastIdContract)
      .pipe(retry(1))
      .subscribe({
        next: (res) => {
          const raw = Array.isArray(res) ? res : [];
          this.validacionTablaData = raw.map((item: any, index: number) => ({
            no: item.NoRegistro ?? item.noRegistro ?? index + 1,
            identificacion: item.Identificacion ?? item.identificacion ?? '',
            nombreUsuario: item.NombreUsuario ?? item.nombreUsuario ?? '',
            parentesco: item.Parentesco ?? item.parentesco ?? '',
            edad: item.Edad ?? item.edad ?? '',
            genero: item.Genero ?? item.genero ?? '',
            fechaNacimiento: item.FechaNacimiento ?? item.fechaNacimiento ?? '',
            fechaAntiguedad: item.FechaAntiguedad ?? item.fechaAntiguedad ?? '',
            anexo: item.Anexo ?? item.anexo ?? '',
            valorTarifaAnexo:
              item.ValorTarifaAnexo ?? item.valorTarifaAnexo ?? '',
            valorTarifaPrograma:
              item.ValorTarifaPrograma ?? item.valorTarifaPrograma ?? '',
            totalTarifa: item.TotalTarifa ?? item.totalTarifa ?? '',
            campaniaVigente: item.CampaniaVigente ?? item.campaniaVigente ?? '',
            congelamiento: item.Congelamiento ?? item.congelamiento ?? '',
            Tarifa: this.pickFirstDefined(
              item,
              [
                'Tarifa',
                'tarifa',
                'TARIFA',
                'NombreTarifa',
                'nombreTarifa',
                'TarifaContrato',
                'tarifaContrato',
              ],
              '',
            ),
            EstructuraTarifaria: this.pickFirstDefined(
              item,
              [
                'EstructuraTarifaria',
                'estructuraTarifaria',
                'estructura_tarifaria',
                'EstructuraTarifa',
                'estructuraTarifa',
              ],
              '',
            ),
          }));

          // Verificar coincidencias SARLAFT para cada integrante
          const paramsList = (this.validacionTablaData || []).map((r) => ({
            nombre: r.nombreUsuario || r.NombreUsuario || '',
            identificacion: r.identificacion || r.Identificacion || '',
          }));

          const checks = paramsList.map((p) => {
            return this.validacionContratoService.consultaCoincidencia(
              p.nombre,
              p.identificacion,
            );
          });

          if (checks.length === 0) {
            this.sarlaftCoincidenciaValida = true;
            this.isLoadingValidacion = false;
            this.saveToLocalStorage(1);
          } else {
            forkJoin(checks).subscribe({
              next: (results) => {
                const found = results.some(
                  (res) => Number(res?.EstaEnLista) === 1,
                );
                if (found) {
                  this.sarlaftCoincidenciaValida = false;
                  const modalRef = this.modalService.open(ModalBasicComponent, {
                    centered: true,
                    windowClass: 'custom-modal-radius',
                  });
                  modalRef.componentInstance.title = 'Advertencia';
                  modalRef.componentInstance.content =
                    'Alguno de los integrantes del contrato seleccionado están reportados en Sarlaft.';
                  modalRef.componentInstance.buttons = [
                    {
                      text: 'Cerrar',
                      color: 'btn-primary',
                      action: 'close',
                      value: true,
                    },
                  ];
                } else {
                  this.sarlaftCoincidenciaValida = true;
                }
                this.isLoadingValidacion = false;
                this.saveToLocalStorage(1);
              },
              error: (err) => {
                console.error('Error al consultar SARLAFT:', err);
                this.sarlaftCoincidenciaValida = false;
                this.isLoadingValidacion = false;
                this.saveToLocalStorage(1);
              },
            });
          }
        },
        error: (err) => {
          this.isLoadingValidacion = false;
          this.errorLoadingContrato = true;
        },
      });
  }

  goToSecondStep() {
    this.setActiveStep(2);
    this.saveToLocalStorage(2);
    this.stepper.steps.get(0)!.editable = false;
    this.stepper.steps.get(0)!.completed = true;
    this.stepper.next();
  }

  // setActiveStep(indexToActivate: number) {
  //   this.items = this.items.map((item, index) => ({
  //     ...item,
  //     collapsed: index !== indexToActivate,
  //     disabled: index !== indexToActivate,
  //   }));
  // }

  setActiveStep(indexToActivate: number) {
    if (indexToActivate > this.maxStepIndexAlcanzado) {
      this.maxStepIndexAlcanzado = indexToActivate;
    }

    this.items = this.items.map((item, index) => ({
      ...item,
      disabled: index > this.maxStepIndexAlcanzado,
      collapsed: index !== indexToActivate,
      readonly:
        index <= this.maxStepIndexAlcanzado && index !== indexToActivate,
    }));
  }

  // resetItems() {
  //   this.items = this.items.map((item) => ({
  //     ...item,
  //     collapsed: true,
  //     disabled: true,
  //   }));
  // }

  resetItems() {
    this.maxStepIndexAlcanzado = 0;

    this.items = this.items.map((item) => ({
      ...item,
      collapsed: true,
      disabled: true,
      readonly: false,
    }));
  }

  updateTooltipPlacement() {
    const width = window.innerWidth;

    this.tooltipPlacement = width < 1200 ? 'bottom' : 'end';
  }

  saveToLocalStorage(activeStep: number) {
    const state = {
      form: this.frmConsult.getRawValue(),
      activeStep,
      isSubmitting: this.isSubmitting,
      selectedContract: this.selectedContract,
      selectedContractUsers: this.selectedContractUsers,
      integrantesSeleccionados: this.integrantesSeleccionados,
      hasSelectionNovedadTraslados: this.hasSelectionNovedadTraslados,
      sarlaftValido: this.sarlaftValido,
      sarlaftCoincidenciaValida: this.sarlaftCoincidenciaValida,
      isAllHealthQuestsValid: this.isAllHealthQuestsValid,
      auditDecision: this.auditDecision,
      contratosData: this.contratosData,
      validacionTablaData: this.validacionTablaData,
      cumplePerfilamiento: this.cumplePerfilamiento,
      noCumplePerfilamiento: this.noCumplePerfilamiento,
      puedeAvanzarPerfilamiento: this.puedeAvanzarPerfilamiento,
      mostrarTablaCumplePerfilamiento: this.mostrarTablaCumplePerfilamiento,
      cuestionarioSaludUsuarios: this.cuestionarioSaludUsuarios,
      resultadosInterfazAuditoria: this.resultadosInterfazAuditoria,
      soportesDocumentales: this.soportesDocumentales,
      currentContract: this.currentContract,
      idSolicitudTemporal: this.idSolicitudTemporal,
      integrantesSolicitudGuardados: this.integrantesSolicitudGuardados,
      maxStepIndexAlcanzado: this.maxStepIndexAlcanzado,
    };

    this.localStorageService.save('frmConsultState', state);
  }

  private itemsIndexToStepperIndex(itemsIndex: number): number {
    return itemsIndex <= 1 ? 0 : itemsIndex - 1;
  }

  loadLocalStorage() {
    const state = this.localStorageService.load('frmConsultState');

    if (!state) return;

    this.frmConsult.patchValue(state.form ?? {}, { emitEvent: false });
    this.updateContractNumberValidator();

    // Si no hay contratos guardados (sesión antigua o datos perdidos),
    // re-habilitar el formulario para que el usuario pueda volver a consultar
    const contratosRestaurados: ContratoAfilmedDTO[] =
      state.contratosData ?? [];
    if (contratosRestaurados.length === 0) {
      this.isSubmitting = false;
      this.frmConsult.enable();
      return;
    }

    this.contratosData = contratosRestaurados;
    this.isSubmitting = state.isSubmitting;

    const steps = this.stepper.steps.toArray();
    const maxItemsIndex = this.items.length - 1 || 0;

    let restoredStep = Number(state.activeStep);

    if (isNaN(restoredStep) || restoredStep < 0) restoredStep = 0;
    if (restoredStep > maxItemsIndex) restoredStep = maxItemsIndex;

    this.maxStepIndexAlcanzado = Number(state.maxStepIndexAlcanzado) || 0;

    this.setActiveStep(restoredStep);

    const maxStepperIndex = (steps.length || 1) - 1;
    const stepperIndex = Math.min(
      this.itemsIndexToStepperIndex(restoredStep),
      maxStepperIndex,
    );

    this.stepper.selectedIndex = stepperIndex;

    const currentIndex = this.stepper.selectedIndex;
    this.selectedContract = state.selectedContract;
    this.filaContratoAfilmedPaso1 = this.obtenerFilaContratosUsers(
      String(this.selectedContract ?? ''),
    );
    this.selectedContractUsers = state.selectedContractUsers;
    this.integrantesSeleccionados = state.integrantesSeleccionados ?? [];
    this.validacionTablaData = state.validacionTablaData ?? [];
    this.hasSelectionNovedadTraslados = state.hasSelectionNovedadTraslados;
    this.sarlaftValido = state.sarlaftValido;
    this.sarlaftCoincidenciaValida = state.sarlaftCoincidenciaValida ?? true;
    this.isAllHealthQuestsValid = state.isAllHealthQuestsValid;
    this.auditDecision = state.auditDecision ?? null;

    this.cumplePerfilamiento = state.cumplePerfilamiento ?? [];
    this.noCumplePerfilamiento = state.noCumplePerfilamiento ?? [];
    this.puedeAvanzarPerfilamiento = state.puedeAvanzarPerfilamiento ?? true;
    this.mostrarTablaCumplePerfilamiento =
      state.mostrarTablaCumplePerfilamiento ?? true;
    this.cuestionarioSaludUsuarios = state.cuestionarioSaludUsuarios ?? [];
    this.resultadosInterfazAuditoria = state.resultadosInterfazAuditoria ?? [];
    this.soportesDocumentales = state.soportesDocumentales ?? [];
    this.currentContract = state.currentContract ?? null;
    this.actualizarTituloDetalleContrato(this.currentContract?.contrato ?? '');
    this.idSolicitudTemporal = state.idSolicitudTemporal || '';
    this.integrantesSolicitudGuardados =
      state.integrantesSolicitudGuardados ?? [];

    if (this.selectedContract && this.contratosData?.length) {
      const filaContratosUsers =
        this.obtenerFilaContratosUsers(this.selectedContract) ??
        this.filaContratoAfilmedPaso1;
      if (filaContratosUsers) {
        void this.resolverColectivoEspecialContrato(
          filaContratosUsers,
          this.selectedContract,
        );
      }
    }

    for (let i = 0; i < currentIndex; i++) {
      steps[i].completed = true;
      steps[i].editable = false;
    }

    if (steps[currentIndex]) {
      steps[currentIndex].completed = false;
    }

    if (this.isSubmitting) {
      this.frmConsult.disable();
    }
  }

  saveHealthQuest(template: TemplateRef<any>) {
    const modalRef = this.modalService.open(ModalBasicComponent, {
      centered: true,
      size: 'lg',
      windowClass: 'custom-modal-radius',
    });

    modalRef.componentInstance.title = 'Validar Auditoría';
    modalRef.componentInstance.content = template;

    modalRef.componentInstance.buttons = [
      {
        text: 'Cancelar',
        color: 'btn-secondary',
        action: 'close',
        value: true,
      },
      {
        text: 'Continuar',
        color: 'btn-primary',
        action: 'custom',
        value: true,
        callback: () => this.goToFourthStep(modalRef),
      },
    ];
  }

  goToFourthStep(modalRef: any) {
    modalRef.close();
    this.validarInterfazAuditoria();
    this.generarIdSolicitudTemporal();

    const snapshotsPayloads: CuestionarioSnapshotRequest[] = [];
    this.cuestionarioCards.forEach((card) => {
      card.getSnapshot().forEach((snapshot: any) => {
        snapshotsPayloads.push({
          ...snapshot,
          idContrato: this.currentContract.contrato,
        });
      });
    });

    this.guardarIntegrantes(snapshotsPayloads);

    this.setActiveStep(4);
    this.saveToLocalStorage(4);
    this.stepper.steps.get(2)!.editable = false;
    this.stepper.steps.get(2)!.completed = true;
    this.stepper.next();
  }

  guardarCopiaCuestionarioSalud(payloads: CuestionarioSnapshotRequest[]): void {
    if (!payloads.length) {
      console.warn(
        'No hay snapshots de cuestionario de salud capturados para guardar.',
      );
      return;
    }
    const peticiones = payloads.map((payload) =>
      this.cuestionarioSaludSnapshotService.guardarSnapshot(payload),
    );
    forkJoin(peticiones).subscribe({
      next: (respuestas) => console.log('Snapshots guardados:', respuestas),
      error: (err) => console.error('Error al guardar snapshots:', err),
    });
  }

  validarInterfazAuditoria(): void {
    const healthQuestData = this.healthQuestCards
      .toArray()
      .flatMap((c) => c.getValues());

    const state = this.getTipoDeNovedadLocalStorage();

    const request: InterfazAuditoriaRequest = {
      lineaNegocioId: this.currentContract.LineaNegocio,
      lineaNegocioCodigo: this.currentContract.LineaNegocio.split('|')[1]
        ?.split('-')[0]
        ?.trim(),
      numeroContrato: this.currentContract.contrato,
      grupoProgramaOrigenId: this.currentContract.GrupoPrograma,
      grupoProgramaDestinoId: state?.grupoProgramaDestino ?? '',
      sucursalContrato: this.currentContract.Sucursal.split('-')[0],
      integrantes: this.cumplePerfilamiento,
      respuestasSalud: healthQuestData,
    };

    this.isLoadingAuditoria = true;
    this.interfazAuditoriaService.validar(request).subscribe({
      next: (response) => {
        this.resultadosInterfazAuditoria = response;
        this.isLoadingAuditoria = false;
        this.saveToLocalStorage(this.stepper.selectedIndex + 1);

        const total = response.length;

        const totalAuditar = response.filter(
          (r) => r.resultado.codigo === 'AUD',
        ).length;

        const totalNoRequiere = response.filter(
          (r) => r.resultado.codigo === 'NRAM',
        ).length;

        const totalNoFactible = response.filter(
          (r) => r.resultado.codigo === 'TNF',
        ).length;

        const totalNoCumple = response.filter(
          (r) => r.resultado.codigo === 'NCP',
        ).length;

        const partes: string[] = [];

        if (totalAuditar > 0) {
          partes.push(
            totalAuditar === 1
              ? '1 requiere evaluación de auditoría médica'
              : `${totalAuditar} requieren evaluación de auditoría médica`,
          );
        }

        if (totalNoRequiere > 0) {
          partes.push(
            totalNoRequiere === 1
              ? '1 no requiere evaluación de auditoría médica'
              : `${totalNoRequiere} no requieren evaluación de auditoría médica`,
          );
        }

        const noFactibles = totalNoFactible + totalNoCumple;

        if (noFactibles > 0) {
          partes.push(
            noFactibles === 1
              ? '1 no cumple con las condiciones, por tanto no es factible su traslado'
              : `${noFactibles} no cumplen con las condiciones, por tanto no es factible su traslado`,
          );
        }

        const mensaje =
          `El proceso de validación de auditoría ha culminado satisfactoriamente. ` +
          `De ${total} ${total === 1 ? 'integrante seleccionado' : 'integrantes seleccionados'}, ` +
          `${partes.join(', ')}.`;

        const modalRef = this.modalService.open(ModalBasicComponent, {
          centered: true,
          windowClass: 'custom-modal-radius',
        });

        modalRef.componentInstance.title = 'Validación de auditoría finalizada';
        modalRef.componentInstance.content = mensaje;

        modalRef.componentInstance.buttons = [
          {
            text: 'Cerrar',
            color: 'btn-primary',
            minWidth: 'none',
            action: 'close',
            value: true,
          },
        ];
      },
      error: (err) => {
        console.error('Error en auditoria:', err);
        const modalRef = this.modalService.open(ModalBasicComponent, {
          centered: true,
          windowClass: 'custom-modal-radius',
        });
        modalRef.componentInstance.title = 'Información';
        modalRef.componentInstance.content =
          'Ha ocurrido un error al ejecutar la validación de auditoría. Por favor intente nuevamente. Si el problema persiste, contacte al administrador del sistema.';
        modalRef.componentInstance.buttons = [
          {
            text: 'Cerrar',
            color: 'btn-primary',
            minWidth: 'none',
            action: 'close',
            value: true,
          },
        ];
        this.isLoadingAuditoria = false;
      },
    });
  }

  setupHealthQuestValidation() {
    if (!this.healthQuestCards) return;

    this.healthQuestCards.forEach((card) => {
      card.form.valueChanges.subscribe(() => {
        this.checkHealthQuestsValidity();
      });
    });

    this.checkHealthQuestsValidity();
  }

  checkHealthQuestsValidity() {
    this.isAllHealthQuestsValid = this.healthQuestCards
      .toArray()
      .every((c) => c.isValid());
  }

  onTipoNovedadSeleccionada(tipoNovedad: TipoNovedad | null): void {
    this.tipoNovedadSeleccionada = tipoNovedad;
    if (!tipoNovedad) {
      this.idTipoSolicitudTraslado = 0;
    } else {
      const idTipoSolicitudSeleccionado = Number(
        tipoNovedad.idTipoSolicitud ?? 0,
      );
      this.idTipoSolicitudTraslado =
        idTipoSolicitudSeleccionado > 0 ? idTipoSolicitudSeleccionado : 0;
    }
    if (this.allSubtiposCatalog && tipoNovedad) {
      const matched = this.allSubtiposCatalog.find(
        (t: any) =>
          String(t.tipoNovedad) === String(tipoNovedad.nombre) ||
          String(t.idTipoNovedad) === String(tipoNovedad.codigo),
      );
      const subs = matched ? matched.subtipos || [] : [];
      this.disponibleSubtipos = subs
        .filter((s: any) => s && (s.estado === 1 || s.estado === '1'))
        .map((s: any) => String(s.subtipoNovedad));
      this.disponibleSubtipos = Array.from(new Set(this.disponibleSubtipos));
    } else if (!tipoNovedad) {
      this.disponibleSubtipos = [];
    }
  }

  onSubtipoNovedadSeleccionada(subtipoNovedad: SubtipoNovedad | null): void {
    this.subtipoNovedadSeleccionada = subtipoNovedad;
  }

  onSubtipoMatSelect(value: string) {
    if (!value) {
      this.onSubtipoNovedadSeleccionada(null);
      return;
    }
    // Buscar objeto de subtipo en catálogo y emitir
    for (const t of this.allSubtiposCatalog) {
      const found = (t.subtipos || []).find(
        (s: any) => String(s.subtipoNovedad) === String(value),
      );
      if (found) {
        this.onSubtipoNovedadSeleccionada(found as SubtipoNovedad);
        return;
      }
    }
    // Si no se encuentra, emitir solo el nombre
    this.onSubtipoNovedadSeleccionada({ id: -1, nombre: value } as any);
  }

  onValidacionCompletada(isValid: boolean): void {
    this.validacionTipoNovedadCompleta = isValid;
  }

  onIntegrantesActualizados(integrantes: IntegranteContrato[]): void {
    this.integrantesSeleccionados = integrantes ?? [];
    // this.selectedContractUsers = integrantes ?? this.selectedContractUsers;
    const activeStep = this.stepper ? this.stepper.selectedIndex + 1 : 1;
    this.saveToLocalStorage(activeStep);
  }

  /** Propaga `TipoPrima` (y campos relacionados) del paso 1 tras `/tipos-prima`. */
  onContratoPaso1Actualizado(partial: Partial<ContratoAfilmedDTO>): void {
    if (!partial || Object.keys(partial).length === 0) {
      return;
    }

    if (this.currentContract) {
      this.currentContract = { ...this.currentContract, ...partial };
    }
    if (this.filaContratoAfilmedPaso1) {
      this.filaContratoAfilmedPaso1 = {
        ...this.filaContratoAfilmedPaso1,
        ...partial,
      };
    }
    if (this.contratoSeleccionado) {
      this.contratoSeleccionado = {
        ...this.contratoSeleccionado,
        ...(partial.TipoPrima != null ? { TipoPrima: partial.TipoPrima } : {}),
        ...(partial.ColectivoEspecial != null
          ? { ColectivoEspecial: partial.ColectivoEspecial }
          : {}),
      };
    }

    const numero = String(this.selectedContract ?? '').trim();
    if (numero && Array.isArray(this.contratosData)) {
      const idx = this.contratosData.findIndex(
        (c) =>
          String(c?.contrato ?? (c as any)?.Contrato ?? '').trim() === numero,
      );
      if (idx >= 0) {
        this.contratosData[idx] = { ...this.contratosData[idx], ...partial };
      }
    }

    const activeStep = this.stepper ? this.stepper.selectedIndex + 1 : 1;
    this.saveToLocalStorage(activeStep);
  }

  /** Fila del listado `contratos-users` por número de contrato (comparación con trim). */
  private obtenerFilaContratosUsers(
    numeroContrato: string,
  ): ContratoAfilmedDTO | null {
    const numero = String(numeroContrato ?? '').trim();
    if (!numero || !Array.isArray(this.contratosData)) {
      return null;
    }
    return (
      this.contratosData.find(
        (c) =>
          String(c?.contrato ?? (c as any)?.Contrato ?? '').trim() === numero,
      ) ?? null
    );
  }

  /** Texto crudo del campo `Nit` del contrato Afilmed. */
  private leerNitCrudoContrato(contrato: ContratoAfilmedDTO | null): string {
    if (!contrato) {
      return '';
    }
    const o = contrato as unknown as Record<string, unknown>;
    const directo = String(
      contrato.Nit ?? o['nit'] ?? o['NIT'] ?? o['nitEmpresa'] ?? '',
    ).trim();
    if (directo) {
      return directo;
    }
    for (const [key, value] of Object.entries(o)) {
      if (!/^nit/i.test(key) || value == null) {
        continue;
      }
      const texto = String(value).trim();
      if (texto) {
        return texto;
      }
    }
    return '';
  }

  /**
   * `"NI - 890300625"` → `"890300625"` (solo lo que va después del separador).
   */
  private parseNitEmpresaValor(raw: string): string {
    const s = String(raw ?? '').trim();
    if (!s) {
      return '';
    }
    const partes = s
      .split(/[-–—|]/u)
      .map((p) => p.trim())
      .filter(Boolean);
    if (partes.length >= 2) {
      const ultimo = partes[partes.length - 1];
      const soloDigitos = ultimo.replace(/\D/g, '');
      return soloDigitos || ultimo;
    }
    const soloDigitos = s.replace(/\D/g, '');
    return soloDigitos || s;
  }

  /**
   * NIT del ítem seleccionado en `POST /contratos-users`.
   * Ej.: `"NI - 890300625"` → `"890300625"`.
   */
  private extraerNitDesdeContratoSeleccionado(
    contrato: ContratoAfilmedDTO | null,
  ): string {
    const raw = this.leerNitCrudoContrato(contrato);
    if (!raw) {
      return '';
    }
    return this.parseNitEmpresaValor(raw);
  }

  /**
   * Tras elegir un contrato del listado `contratos-users` (clic en el ojo),
   * consulta colectivo especial con su `Nit` o asigna `ColectivoEspecial: "N"`.
   */
  private async resolverColectivoEspecialContrato(
    contratoContratosUsers: ContratoAfilmedDTO,
    numeroContrato: string,
  ): Promise<void> {
    const numero = String(numeroContrato ?? '').trim();
    const reqId = ++this.colectivoEspecialRequestId;
    const filaContratosUsers =
      this.obtenerFilaContratosUsers(numero) ?? contratoContratosUsers;
    const nit = this.extraerNitDesdeContratoSeleccionado(filaContratosUsers);

    if (!nit) {
      if (
        reqId === this.colectivoEspecialRequestId &&
        String(this.selectedContract ?? '').trim() === numero
      ) {
        this.onContratoPaso1Actualizado({ ColectivoEspecial: 'N' });
      }
      return;
    }

    try {
      const resp = await firstValueFrom(
        this.colectivoEspecialService.consultarColectivoEspecial({ nit }),
      );
      if (reqId !== this.colectivoEspecialRequestId) {
        return;
      }
      if (String(this.selectedContract ?? '').trim() !== numero) {
        return;
      }

      const colectivoEspecial = resp?.colectivoEspecial === 'S' ? 'S' : 'N';
      this.onContratoPaso1Actualizado({ ColectivoEspecial: colectivoEspecial });
    } catch (error) {
      console.error('Error consultando colectivo especial:', error);
    }
  }

  onDecisionAuditoria(decision: 'si' | 'no' | null): void {
    this.auditDecision = decision;
    this.saveToLocalStorage(this.stepper.selectedIndex + 1);
  }

  /** Antes del blur de los mat-select al pulsar «Siguiente» (mousedown va antes que selectionChange). */
  onAntesSiguienteTipoNovedad(): void {
    this.tipoNovedadSelectorBridge.prepararAvanceDePaso();
  }

  private restaurarTipoNovedadStateSiDegradado(
    respaldo: Record<string, unknown> | null,
  ): void {
    if (!respaldo) {
      return;
    }
    const actual = this.getTipoDeNovedadLocalStorage();
    const base = actual ?? {};
    let cambio = false;
    const fusionado: Record<string, unknown> = { ...base };
    for (const campo of this.camposTipoNovedadCriticos) {
      if (fusionado[campo] == null && respaldo[campo] != null) {
        fusionado[campo] = respaldo[campo];
        cambio = true;
      }
    }
    if (!actual || cambio) {
      this.localStorageService.save(TIPO_NOVEDAD_STORAGE_KEY, fusionado);
    }
  }

  goToThirdStep(): void {
    if (this.validacionTipoNovedadCompleta) {
      const respaldoAntes = this.getTipoDeNovedadLocalStorage();
      this.tipoNovedadSelectorBridge.prepararAvanceDePaso();
      const respaldo =
        this.getTipoDeNovedadLocalStorage() ?? respaldoAntes ?? null;

      this.loadInfoContrato(
        this.currentContract,
        this.integrantesSeleccionados,
      );
      this.setActiveStep(3);
      this.stepper.steps.get(1)!.editable = false;
      this.stepper.steps.get(1)!.completed = true;
      this.stepper.next();

      this.restaurarTipoNovedadStateSiDegradado(respaldo);
      setTimeout(
        () => this.restaurarTipoNovedadStateSiDegradado(respaldo),
        350,
      );
      setTimeout(
        () => this.restaurarTipoNovedadStateSiDegradado(respaldo),
        800,
      );
    }
  }

  private procesarResultadoPerfilamiento(
    response: PerfilamientoResponse,
    contrato: ContratoAfilmedDTO,
  ): void {
    // Llenar datasources de las tablas
    this.cumplePerfilamiento = response.cumplePerfilamiento;
    this.noCumplePerfilamiento = response.noCumplePerfilamiento;

    //Llenar cards de cuestionario de salud (solo los que cumplen)

    const state = this.getTipoDeNovedadLocalStorage();
    this.cuestionarioSaludUsuarios = response.cumplePerfilamiento.map(
      (usuario) => ({
        nombreUsuario: usuario.NombreUsuario,
        identificacion: usuario.Identificacion,
        parentescoId: usuario.Parentesco.split('|')[0].trim(),
        edad: usuario.Edad,
        generoId: usuario.Genero === 'M' ? 1 : 2,
        lineaNegocioId: contrato.LineaNegocio.split('|')[0].trim(),
        grupoProgramaId: contrato.GrupoPrograma.split('|')[0].trim(),
        tipoSolicitudId: state?.selectedTipoNovedad.idTipoSolicitud ?? null,
        tipoNovedadId: state?.selectedTipoNovedad.codigo ?? null,
      }),
    );
  }

  onContinuarRadicacion(): void {
    this.consultarSoportes();
    this.setActiveStep(5);
    this.stepper.steps.get(3)!.editable = false;
    this.stepper.steps.get(3)!.completed = true;
    this.stepper.next();
  }

  onSarlaftEstado(valido: boolean) {
    this.sarlaftValido = valido;
    this.saveEstadoProceso();
  }

  onAutorizacionEstado(valido: boolean) {
    this.autorizacionValida = valido;
    this.saveEstadoProceso();
  }

  onSoporteEstado(index: number, completo: boolean) {
    this.soportesCompletos[index] = completo;
    this.saveEstadoProceso();
  }

  get mostrarBotonRadicarPendiente(): boolean {
    const existeSoporteIncompleto = this.soportesCompletos.some(
      (c) => c === false,
    );

    return (
      this.sarlaftValido && this.autorizacionValida && existeSoporteIncompleto
    );
  }

  get mostrarBotonPendienteFirma(): boolean {
    const existeSoporteCompleto = this.soportesCompletos.every(
      (c) => c === true,
    );

    return (
      this.sarlaftValido && this.autorizacionValida && existeSoporteCompleto
    );
  }

  get solicitudNumeroGenerada(): string {
    return this.solicitudRadicadaId != null
      ? String(this.solicitudRadicadaId)
      : '';
  }

  get plantillaNombreGenerada(): string {
    const first = this.documentosGeneradosSolicitud[0];
    return first?.nombreClaveJasper || 'Generación automática';
  }

  get cantidadAnexosGenerados(): number {
    return this.documentosGeneradosSolicitud.filter((d) =>
      String(d?.codigoDocumento ?? '')
        .trim()
        .toUpperCase()
        .includes('ANX'),
    ).length;
  }

  private saveEstadoProceso(): void {
    this.localStorageService.save('estado_proceso', {
      sarlaftValido: this.sarlaftValido,
      autorizacionValida: this.autorizacionValida,
      soportesCompletos: this.soportesCompletos,
    });
  }

  private restoreEstadoProceso(): void {
    const saved = this.localStorageService.load('estado_proceso');

    if (!saved) return;

    this.sarlaftValido = !!saved.sarlaftValido;
    this.autorizacionValida = !!saved.autorizacionValida;
    this.soportesCompletos = saved.soportesCompletos ?? [];
  }

  radicarSolicitud(tipo: string): void {
    this.resetEstadoGeneracionDocumentos();
    this.isRadicando = true;

    const estadoLs = this.getTipoDeNovedadLocalStorage();
    this.idTipoSolicitudCapturadoParaDocumentos =
      this.resolverIdTipoSolicitudParaTraslados(estadoLs);

    const radicarBody = this.buildRadicarSolicitudRequest(tipo);
    this.logContratoOrigenParaRevisionBd(radicarBody);

    const idTipoNovedad = radicarBody.datosTipoNovedad?.idTipoNovedad;
    const numeroContrato =
      radicarBody.contratoOrigen?.numeroContrato || radicarBody.numeroContrato;

    this.validarDuplicadoService
      .validarDuplicado({
        idTipoNovedad,
        idSubtipoNovedad:
          radicarBody.datosTipoNovedad?.idSubtipoNovedad ?? null,
        numeroContrato,
      })
      .subscribe({
        next: (validacion) => {
          if (validacion?.existeDuplicado) {
            this.isRadicando = false;
            this.mostrarModalSolicitudDuplicada(validacion, radicarBody, tipo);
          } else {
            this.ejecutarRadicacion(radicarBody, tipo);
          }
        },
        error: (err) => {
          console.error('Error al validar solicitud duplicada:', err);
        },
      });
  }

  private mostrarModalSolicitudDuplicada(
    validacion: {
      nroSolicitudTraslado?: number | null;
      mensaje?: string | null;
    },
    radicarBody: RadicarSolicitudRequest,
    tipo: string,
  ): void {
    const modalRef = this.modalService.open(ModalBasicComponent, {
      centered: true,
      windowClass: 'custom-modal-radius',
    });

    modalRef.componentInstance.title = 'Solicitud en gestión';
    modalRef.componentInstance.content =
      validacion.mensaje ||
      `Ya existe una solicitud en gestión con el número ${validacion.nroSolicitudTraslado} radicada. ¿Desea registrar la novedad?`;

    modalRef.componentInstance.buttons = [
      {
        text: 'Cancelar',
        color: 'btn-secondary',
        minWidth: 'none',
        action: 'close',
        value: true,
      },
      {
        text: 'Aceptar',
        color: 'btn-primary',
        minWidth: 'none',
        action: 'custom',
        value: true,
        callback: () => {
          modalRef.close();
          const bodyConfirmado: RadicarSolicitudRequest = {
            ...radicarBody,
            confirmarDuplicado: true,
          };
          this.ejecutarRadicacion(bodyConfirmado, tipo);
        },
      },
    ];
  }

  private ejecutarRadicacion(
    radicarBody: RadicarSolicitudRequest,
    tipo: string,
  ): void {
    this.isRadicando = true;

    this.radicarSolicitudService.radicarSolicitud(radicarBody).subscribe({
      next: (resp) => {
        this.solicitudRadicadaId =
          Number(resp?.idSolicitudesRadicadas ?? 0) || null;

        this.isRadicando = false;

        if (this.solicitudRadicadaId) {
          const modalRef = this.modalService.open(ModalBasicComponent, {
            centered: true,
            windowClass: 'custom-modal-radius',
          });

          modalRef.componentInstance.title = 'Éxito';
          modalRef.componentInstance.content = `La solicitud de traslado ${this.solicitudRadicadaId} se registró exitosamente.`;

          modalRef.componentInstance.buttons = [
            {
              text: 'Cerrar',
              color: 'btn-secondary',
              minWidth: 'none',
              action: 'custom',
              value: true,
              callback: async () => {
                modalRef.close();

                this.modalRef = this.modalService.open(ModalBasicComponent, {
                  centered: true,
                  backdrop: 'static',
                  keyboard: false,
                  size: 'lg',
                  windowClass: 'custom-modal-radius',
                });

                this.modalRef.componentInstance.title = '';
                this.modalRef.componentInstance.content = this.progressContent;
                this.modalRef.componentInstance.buttons = [];
                this.modalRef.componentInstance.showCloseButton = false;

                await this.ejecutarFlujoGeneracionDocumentos(
                  this.solicitudRadicadaId!,
                  tipo,
                );
              },
            },
            {
              text: 'Cancelar',
              color: 'btn-primary',
              minWidth: 'none',
              action: 'custom',
              value: true,
              callback: () => {
                modalRef.close();
              },
            },
          ];
        } else {
          this.marcarErrorGeneracion(
            1,
            'No fue posible identificar la solicitud radicada.',
          );
        }
      },
      error: (err) => {
        console.error('Error al radicar', err);

        // Defensa en profundidad: si el backend devuelve 409 (duplicado detectado
        // pese a haber pasado la validación previa), mostramos el modal de duplicado.
        if (err?.status === 409 && err?.error?.existeDuplicado) {
          this.mostrarModalSolicitudDuplicada(err.error, radicarBody, tipo);
          this.isRadicando = false;
          return;
        }

        const modalRef = this.modalService.open(ModalBasicComponent, {
          centered: true,
          windowClass: 'custom-modal-radius',
        });
        modalRef.componentInstance.title = 'Error al radicar la solicitud';
        modalRef.componentInstance.content =
          err.error?.error || 'Error inesperado';
        modalRef.componentInstance.buttons = [
          {
            text: 'Cerrar',
            color: 'btn-primary',
            action: 'close',
            value: true,
          },
        ];
        this.isRadicando = false;
      },
    });
  }

  private resetEstadoGeneracionDocumentos(): void {
    this.documentosGeneradosSolicitud = [];
    this.validacionFirmaSolicitud = null;
    this.enlaceFirmaGenerado = null;
    this.errorEnlaceFirma = null;
    this.ultimoErrorDocumentosGenerados = null;
    this.ultimoErrorValidarFirma = null;
    this.solicitudRadicadaId = null;
    this.pasosGeneracionDocumentos = [
      { label: 'Radicando solicitud', status: 'completed' },
      {
        label: 'Generando documentos',
        status: 'in-progress',
      },
      { label: 'Validando documentos para firma', status: 'pending' },
    ];
  }

  /**
   * (1) `documentos-parametrizados` → `nombreClaveJasper`;
   * (2) `reports/{clave}` una vez por formato; `persistir-ruta` por cada integrante × formato;
   * (3) `validar-firma` (GET opcional de resumen tras éxito).
   */
  private async ejecutarFlujoGeneracionDocumentos(
    solicitudRadicadaId: number,
    tipoRadicacion: string,
  ): Promise<void> {
    this.ultimoErrorDocumentosGenerados = null;
    this.ultimoErrorValidarFirma = null;
    this.actualizarPasoGeneracion(0, 'completed');
    this.actualizarPasoGeneracion(1, 'in-progress');

    const seleccionDocumentos =
      this.construirSeleccionDocumentosParaGeneracion();
    if (!seleccionDocumentos) {
      const state = this.obtenerEstadoTipoNovedadParaDocumentos();
      const idTipo = this.resolverIdTipoSolicitudParaTraslados(state);
      const contrato = this.currentContract as ContratoAfilmedDTO | any;
      const lineaOrigen = this.valorLineaGrupoParametrizacion(
        contrato?.LineaNegocio ?? contrato?.lineaNegocio,
      );
      const grupoOrigen = this.valorLineaGrupoParametrizacion(
        contrato?.GrupoPrograma ??
          contrato?.grupoPrograma ??
          contrato?.ProgramaCodigo,
      );
      let detalle =
        'No se pudo armar la selección de documentos para generar PDFs.';
      if (!idTipo) {
        const st = state as {
          selectedTipoNovedad?: { nombre?: string };
          tipoNovedad?: { nombre?: string };
        } | null;
        const nombreTipo =
          st?.selectedTipoNovedad?.nombre ??
          st?.tipoNovedad?.nombre ??
          '(desconocido)';
        detalle = `El tipo de novedad «${nombreTipo}» no tiene idTipoSolicitud configurado en el catálogo (GET tipos-novedad). Verifique la parametrización en TRL_P_TIPO_NOVEDAD_TIPO_SOLICITUD.`;
      } else if (!lineaOrigen || !grupoOrigen) {
        detalle =
          'Faltan línea de negocio o grupo programa del contrato origen para consultar documentos parametrizados.';
      }
      this.marcarErrorGeneracion(1, detalle);
      return;
    }

    let plantillas: DocumentoFormatoDTO[] = [];
    try {
      plantillas = await firstValueFrom(
        this.generacionDocumentosService.consultarDocumentosParametrizados(
          seleccionDocumentos,
        ),
      );
    } catch (error: any) {
      this.marcarErrorGeneracion(
        1,
        await this.resolverMensajeErrorRespuesta(error),
      );
      return;
    }

    const conClave = (plantillas ?? []).filter(
      (d) => d?.nombreClaveJasper && String(d.nombreClaveJasper).trim(),
    );
    if (conClave.length === 0) {
      this.marcarErrorGeneracion(
        1,
        'No se encontraron documentos configurados para la combinación seleccionada de tipo de solicitud, línea de negocio y grupo programa. Verifique la parametrización en el módulo ADMINISTRACIÓN | Reglas de negocio | Parametrización de formatos.',
      );
      return;
    }

    const idsIntegrantes =
      this.obtenerIdsIntegrantesParaPersistenciaDocumentos();
    if (idsIntegrantes.length === 0) {
      this.marcarErrorGeneracion(
        1,
        'No hay integrantes guardados para registrar los documentos generados. Complete el paso de perfilamiento antes de radicar.',
      );
      return;
    }

    for (const doc of conClave) {
      const clave = String(doc.nombreClaveJasper).trim();
      try {
        if (this.esDocumentoCartaNegacion(doc)) {
          for (const integrante of this.integrantesSolicitudGuardados) {
            const cedula = String(integrante.nroDocumento ?? '').trim();
            if (!cedula) {
              this.marcarErrorGeneracion(
                1,
                `Falta la cédula del integrante ${integrante.nombreUsuario || '(sin nombre)'} para generar ${doc.nombreDocumento || 'carta de negación'}.`,
              );
              return;
            }
            const generado = await firstValueFrom(
              this.generacionDocumentosService.generarPdfCartaNegacionTrasladoConDetalle(
                solicitudRadicadaId,
                cedula,
              ),
            );
            const rutaArchivo = await this.resolverRutaArchivoTrasGenerarPdf(
              generado,
              doc,
              'negacion_traslado',
            );
            if (!rutaArchivo) {
              this.marcarErrorGeneracion(
                1,
                `No se obtuvo ruta en file server para ${doc.nombreDocumento || clave} (integrante ${cedula}).`,
              );
              return;
            }
            await firstValueFrom(
              this.generacionDocumentosService.persistirRutaDocumentoGenerado({
                idIntegrantesSolicitudRadicacion: integrante.id,
                idPContratosFormatos: doc.idPContratosFormatos,
                rutaArchivo,
              }),
            );
          }
          continue;
        }

        const generado = await firstValueFrom(
          this.generacionDocumentosService.generarPdfPorRadicacionYClaveConDetalle(
            clave,
            solicitudRadicadaId,
          ),
        );
        const rutaArchivo = await this.resolverRutaArchivoTrasGenerarPdf(
          generado,
          doc,
          clave,
        );
        if (!rutaArchivo) {
          this.marcarErrorGeneracion(
            1,
            `No se obtuvo ruta en file server para el documento ${doc.nombreDocumento || clave}.`,
          );
          return;
        }
        for (const idIntegrante of idsIntegrantes) {
          await firstValueFrom(
            this.generacionDocumentosService.persistirRutaDocumentoGenerado({
              idIntegrantesSolicitudRadicacion: idIntegrante,
              idPContratosFormatos: doc.idPContratosFormatos,
              rutaArchivo,
            }),
          );
        }
      } catch (error: any) {
        this.marcarErrorGeneracion(
          1,
          await this.resolverMensajeErrorRespuesta(error),
        );
        return;
      }
    }

    this.actualizarPasoGeneracion(1, 'completed');

    if (tipoRadicacion === 'Radicado Pendiente por soporte') {
      try {
        const docs = await firstValueFrom(
          this.generacionDocumentosService.consultarDocumentosGenerados(
            solicitudRadicadaId,
          ),
        );

        if (Array.isArray(docs)) {
          this.documentosGeneradosSolicitud = docs;
        }
      } catch (error: any) {
        this.ultimoErrorDocumentosGenerados =
          await this.resolverMensajeErrorRespuesta(error);
      }

      this.enlaceFirmaGenerado = null;
      this.errorEnlaceFirma = null;
      this.validacionFirmaSolicitud = null;

      this.onProgressCompleted();
      return;
    }

    this.actualizarPasoGeneracion(2, 'in-progress');

    const firmaOk = await this.validarCompletitudParaFirma(solicitudRadicadaId);

    if (firmaOk) {
      try {
        const docs = await firstValueFrom(
          this.generacionDocumentosService.consultarDocumentosGenerados(
            solicitudRadicadaId,
          ),
        );

        if (Array.isArray(docs) && docs.length > 0) {
          this.documentosGeneradosSolicitud = docs;
        }
      } catch (error: any) {
        this.ultimoErrorDocumentosGenerados =
          await this.resolverMensajeErrorRespuesta(error);
      }

      await this.finalizarGeneracionConExito();
      return;
    }

    const baseMsg = this.buildMensajeFaltantesFirma();

    const sufixValidar = this.ultimoErrorValidarFirma
      ? ` Error en validar-firma: ${this.ultimoErrorValidarFirma}`
      : '';

    this.marcarErrorGeneracion(2, `${baseMsg}${sufixValidar}`);
  }

  /** PK de cada fila en `TRL_INTEGRANTES_SOLICITUD_RADICACION` (persistir-ruta × integrante). */
  private obtenerIdsIntegrantesParaPersistenciaDocumentos(): number[] {
    return this.integrantesSolicitudGuardados
      .map((i) => Number(i.id))
      .filter((id) => Number.isFinite(id) && id > 0);
  }

  /** Carta de negación / carta de negación BG desde `documentos-parametrizados`. */
  private esDocumentoCartaNegacion(doc: DocumentoFormatoDTO): boolean {
    const texto = `${doc.nombreDocumento ?? ''} ${doc.codigoDocumento ?? ''}`
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
    return (
      texto.includes('carta de negacion bg') ||
      texto.includes('carta de negacion')
    );
  }

  private async resolverRutaArchivoTrasGenerarPdf(
    generado: { pdf: Blob; rutaArchivo: string },
    doc: DocumentoFormatoDTO,
    claveJasper: string,
  ): Promise<string> {
    const desdeBff = String(generado.rutaArchivo ?? '').trim();
    if (desdeBff) {
      return desdeBff;
    }
    const nombre = String(
      doc.codigoDocumento || doc.nombreDocumento || claveJasper || 'documento',
    )
      .replace(/[^\w.-]+/g, '_')
      .trim();
    const file = new File([generado.pdf], `${nombre || 'documento'}.pdf`, {
      type: 'application/pdf',
    });
    return String(
      await firstValueFrom(this.fileServerService.subirArchivo(file)),
    ).trim();
  }

  private async validarCompletitudParaFirma(
    solicitudRadicadaId: number,
  ): Promise<boolean> {
    this.ultimoErrorValidarFirma = null;
    try {
      const validacion = await firstValueFrom(
        this.generacionDocumentosService.validarSolicitudCompletaParaFirma(
          solicitudRadicadaId,
        ),
      );
      this.validacionFirmaSolicitud = validacion ?? null;
      return !!validacion?.completaParaFirma;
    } catch (e: any) {
      this.ultimoErrorValidarFirma =
        await this.resolverMensajeErrorRespuesta(e);
      return false;
    }
  }

  private buildMensajeFaltantesFirma(): string {
    const v = this.validacionFirmaSolicitud;
    const faltantes = v?.faltantes ?? [];
    if (faltantes.length === 0) {
      if (
        v &&
        typeof v.totalGeneradosConRuta === 'number' &&
        typeof v.totalEsperados === 'number'
      ) {
        return `La solicitud no está completa para firma (${v.totalGeneradosConRuta} de ${v.totalEsperados} documentos con ruta registrada).`;
      }
      return 'No se encontraron documentos completos para firma en la solicitud.';
    }
    const primero = faltantes[0];
    const nombre = String(
      primero?.nombreClaveJasper ?? primero?.nombreDocumento ?? '',
    ).trim();
    return nombre
      ? `La solicitud aún no está completa para firma. Falta plantilla: ${nombre}.`
      : 'La solicitud aún no está completa para firma.';
  }

  /**
   * Reintentos GET `documentos-generados`. No bloquea el POST a Jasper con 8×1,5 s si aún no hay filas.
   * Si el GET falla en HTTP varias veces seguidas, se corta (no tiene sentido esperar 12 s más).
   */
  private async consultarDocumentosGeneradosConReintentos(
    solicitudRadicadaId: number,
    options?: {
      maxIntentos?: number;
      sleepMs?: number;
      salirTrasErroresHttpConsecutivos?: number;
    },
  ): Promise<{
    lista: DocumentoGeneradoSolicitudDTO[];
    ultimoErrorHttp?: string;
  }> {
    const maxIntentos = options?.maxIntentos ?? 8;
    const sleepMs = options?.sleepMs ?? 1500;
    const salirTrasErroresHttpConsecutivos =
      options?.salirTrasErroresHttpConsecutivos ?? 3;

    let ultimoErrorHttp: string | undefined;
    let erroresHttpSeguidos = 0;

    for (let i = 0; i < maxIntentos; i++) {
      try {
        const docs = await firstValueFrom(
          this.generacionDocumentosService.consultarDocumentosGenerados(
            solicitudRadicadaId,
          ),
        );
        const lista = Array.isArray(docs) ? docs : [];
        if (lista.length > 0) {
          return { lista };
        }
        ultimoErrorHttp = undefined;
        erroresHttpSeguidos = 0;
      } catch (error: any) {
        ultimoErrorHttp = await this.resolverMensajeErrorRespuesta(error);
        erroresHttpSeguidos++;
        if (erroresHttpSeguidos >= salirTrasErroresHttpConsecutivos) {
          return { lista: [], ultimoErrorHttp };
        }
      }
      if (i < maxIntentos - 1) {
        await this.sleep(sleepMs);
      }
    }
    return { lista: [], ultimoErrorHttp };
  }

  /**
   * Texto para `SeleccionDocumentosRequest`: debe coincidir (TRIM/UPPER) con
   * `TRL_P_CONTRATOS_FORMATOS` — puede ser solo código o cadena compuesta
   * (`"2 | SAO-SALUD ORAL"`) según parametrización (CONSUMO-GENERACION-DOCUMENTOS-FRONTEND.md §2).
   */
  private valorLineaGrupoParametrizacion(raw: unknown): string {
    return this.textoParametrizacionDesdeFuente(raw)
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Resuelve string desde contrato, mat-select u objeto catálogo.
   * Prioriza etiquetas compuestas cuando existen (nombre/descripcion antes que solo codigo).
   */
  private textoParametrizacionDesdeFuente(raw: unknown): string {
    if (raw == null) {
      return '';
    }
    if (typeof raw === 'string' || typeof raw === 'number') {
      return String(raw);
    }
    if (typeof raw === 'object') {
      const o = raw as Record<string, unknown>;
      const candidatos = [
        o['nombre'],
        o['Nombre'],
        o['descripcion'],
        o['Descripcion'],
        o['label'],
        o['texto'],
        o['valor'],
        o['lineaNegocio'],
        o['LineaNegocio'],
        o['grupoPrograma'],
        o['GrupoPrograma'],
        o['codigo'],
        o['Codigo'],
        o['id'],
        o['Id'],
      ];
      for (const c of candidatos) {
        const t = String(c ?? '').trim();
        if (t) {
          return t;
        }
      }
    }
    return '';
  }

  /**
   * Línea/grupo destino desde el estado del selector (traslado a contrato nuevo/existente).
   * El backend prioriza destino cuando viene informado.
   */
  private resolverLineaGrupoDestinoDesdeEstado(state: any): {
    linea: string;
    grupo: string;
  } {
    if (!state) {
      return { linea: '', grupo: '' };
    }
    const grupoFlat = this.valorLineaGrupoParametrizacion(
      state.grupoProgramaDestino,
    );
    const lineaEx = this.valorLineaGrupoParametrizacion(
      state.lineaNegocioDestinoExistente,
    );
    const grupoEx = this.valorLineaGrupoParametrizacion(
      state.grupoProgramaDestinoExistente,
    );
    const contratoDest = state.contratoDestino;
    const lineaCd = this.valorLineaGrupoParametrizacion(
      contratoDest?.LineaNegocio ??
        contratoDest?.lineaNegocio ??
        contratoDest?.idLineaNegocio,
    );
    const grupoCd = this.valorLineaGrupoParametrizacion(
      contratoDest?.GrupoPrograma ??
        contratoDest?.grupoPrograma ??
        contratoDest?.ProgramaCodigo,
    );
    const lineaProg = this.valorLineaGrupoParametrizacion(
      state.programaDestinoContrato?.LineaNegocio ??
        state.programaDestinoContrato?.lineaNegocio,
    );
    const grupoProg = this.valorLineaGrupoParametrizacion(
      state.programaDestinoContrato?.GrupoPrograma ??
        state.programaDestinoContrato?.grupoPrograma,
    );
    const lineaPlan = this.valorLineaGrupoParametrizacion(
      state.programaDestino?.LineaNegocio ??
        state.programaDestino?.lineaNegocio,
    );
    const grupoPlan = this.valorLineaGrupoParametrizacion(
      state.programaDestino?.GrupoPrograma ??
        state.programaDestino?.grupoPrograma,
    );
    const lineaCol = this.valorLineaGrupoParametrizacion(
      state.programaDestinoColectivo?.LineaNegocio ??
        state.programaDestinoColectivo?.lineaNegocio,
    );
    const grupoCol = this.valorLineaGrupoParametrizacion(
      state.programaDestinoColectivo?.GrupoPrograma ??
        state.programaDestinoColectivo?.grupoPrograma ??
        state.programaDestinoColectivo?.grupoProgramaTexto,
    );

    const linea = this.valorLineaGrupoParametrizacion(
      lineaEx || lineaCd || lineaProg || lineaPlan || lineaCol || '',
    );
    const grupo = this.valorLineaGrupoParametrizacion(
      grupoFlat ||
        grupoEx ||
        grupoCd ||
        grupoProg ||
        grupoPlan ||
        grupoCol ||
        '',
    );
    return { linea, grupo };
  }

  /**
   * Errores de `HttpClient` con `responseType: 'blob'` traen el JSON de error como `Blob`;
   * hay que leerlo en texto para mostrar `message` (p. ej. 503 «Acceso prohibido» en reportes).
   */
  private async resolverMensajeErrorRespuesta(error: any): Promise<string> {
    const status = Number(error?.status ?? 0);
    const raw = error?.error;
    if (raw instanceof Blob) {
      try {
        const text = (await raw.text()).trim();
        if (text.startsWith('{')) {
          const o = JSON.parse(text) as Record<string, unknown>;
          const inner = String(
            o['message'] ?? o['mensaje'] ?? o['error'] ?? o['detail'] ?? '',
          ).trim();
          if (inner) {
            const base = status ? `[${status}] ${inner}` : inner;
            if (status === 503) {
              return `${base} Si aparece «Acceso prohibido», el backend no autorizó el llamado al motor de reportes (revise SERVICE_URL_REPORTS, USER_NAME y USER_PASSWORD en el servidor).`;
            }
            return base;
          }
        }
        if (text) {
          const cut = text.length > 400 ? `${text.slice(0, 400)}…` : text;
          return status ? `[${status}] ${cut}` : cut;
        }
      } catch {
        /* ignore */
      }
      return (
        (status ? `[${status}] ` : '') +
        'El servidor devolvió cuerpo no legible (posible JSON de error en respuesta tipo PDF/Blob).'
      );
    }
    return this.extraerMensajeErrorHttp(error);
  }

  private extraerMensajeErrorHttp(error: any): string {
    const status = Number(error?.status ?? 0);
    const e = error?.error;
    let msg = '';
    if (typeof e === 'string' && e.trim()) {
      msg = e.trim();
    } else if (e && typeof e === 'object') {
      msg = String(
        e.message ?? e.mensaje ?? e.error ?? e.descripcion ?? e.detail ?? '',
      ).trim();
    }
    if (!msg) {
      msg =
        String(error?.message ?? '').trim() ||
        'Falló la generación de documentos.';
    }
    if (status === 503) {
      return `${msg} Si aparece «Acceso prohibido», el backend no autorizó el llamado al motor de reportes (revise SERVICE_URL_REPORTS, USER_NAME y USER_PASSWORD en el servidor).`;
    }
    return msg;
  }

  private construirSeleccionDocumentosParaGeneracion(): SeleccionDocumentosRequest | null {
    const state = this.obtenerEstadoTipoNovedadParaDocumentos();
    const idTipoSolicitud = this.resolverIdTipoSolicitudParaTraslados(state);

    const contrato = this.currentContract as ContratoAfilmedDTO | any;
    const idLineaNegocioOrigen =
      contrato?.LineaNegocio.split('|')[0].trim() ??
      contrato?.lineaNegocio.split('|')[0].trim();

    const idGrupoProgramaOrigen =
      contrato?.GrupoPrograma.split('|')[0].trim() ??
      contrato?.grupoPrograma.split('|')[0].trim();

    const dest = this.resolverLineaGrupoDestinoDesdeEstado(state);
    const idGrupoProgramaDestino = dest.grupo
      ? String(dest.grupo).split('|')[0].trim()
      : '';

    const lineaEfectiva = idLineaNegocioOrigen;
    const grupoEfectivo = idGrupoProgramaDestino || idGrupoProgramaOrigen;

    if (!idTipoSolicitud) {
      const st = state as {
        idTipoSolicitud?: unknown;
        selectedTipoNovedad?: unknown;
        tipoNovedad?: unknown;
      } | null;
      console.warn(
        '[documentos-parametrizados] Falta idTipoSolicitud. Estado:',
        {
          idTipoSolicitud: st?.['idTipoSolicitud'],
          selectedTipoNovedad: st?.['selectedTipoNovedad'],
          tipoNovedad: st?.['tipoNovedad'],
          capturado: this.idTipoSolicitudCapturadoParaDocumentos,
        },
      );
      return null;
    }
    if (!lineaEfectiva || !grupoEfectivo) {
      console.warn('[documentos-parametrizados] Falta línea o grupo. Origen:', {
        idLineaNegocioOrigen,
        idGrupoProgramaOrigen,
        idGrupoProgramaDestino,
        dest,
      });
      return null;
    }

    const payload: SeleccionDocumentosRequest = {
      idTipoSolicitud,
      idLineaNegocioOrigen,
      idGrupoProgramaOrigen,
    };

    if (idGrupoProgramaDestino) {
      payload.idGrupoProgramaDestino = idGrupoProgramaDestino;
    }

    return payload;
  }

  /**
   * Solo para `documentos-parametrizados`: localStorage es la fuente de verdad
   * (`tarifaDestinoContrato`, programas, etc.). El bridge solo enriquece
   * `idTipoSolicitud` si el LS no lo trae (acordeón paso 2 colapsado).
   */
  private obtenerEstadoTipoNovedadParaDocumentos(): any {
    const fromLs = this.getTipoDeNovedadLocalStorage();
    const fromBridge = this.tipoNovedadSelectorBridge.obtenerEstadoActual();

    if (!fromLs) {
      return fromBridge ?? null;
    }
    if (!fromBridge) {
      return fromLs;
    }

    const idTipoSolicitud =
      this.resolverIdTipoSolicitudParaTraslados(fromLs) ||
      this.resolverIdTipoSolicitudParaTraslados(fromBridge) ||
      null;

    const enriquecido: Record<string, unknown> = { ...fromLs };

    if (idTipoSolicitud) {
      enriquecido['idTipoSolicitud'] = idTipoSolicitud;
    }

    const selectedLs = fromLs['selectedTipoNovedad'];
    const selectedBr = fromBridge['selectedTipoNovedad'];
    if (selectedLs || selectedBr) {
      const base = selectedLs ?? selectedBr;
      enriquecido['selectedTipoNovedad'] =
        idTipoSolicitud && base
          ? { ...(base as object), idTipoSolicitud }
          : base;
    }

    const tipoLs = fromLs['tipoNovedad'];
    const tipoBr = fromBridge['tipoNovedad'];
    if (tipoLs || tipoBr) {
      const base = tipoLs ?? tipoBr;
      enriquecido['tipoNovedad'] =
        idTipoSolicitud && base
          ? { ...(base as object), idTipoSolicitud }
          : base;
    }

    return enriquecido;
  }

  private async finalizarGeneracionConExito(): Promise<void> {
    this.actualizarPasoGeneracion(1, 'completed');
    this.actualizarPasoGeneracion(2, 'completed');
    const id = this.solicitudRadicadaId;
    this.enlaceFirmaGenerado = null;
    this.errorEnlaceFirma = null;

    if (id != null) {
      try {
        this.enlaceFirmaGenerado = await firstValueFrom(
          this.firmaService.generarEnlace(id),
        );
      } catch (err) {
        this.errorEnlaceFirma = await this.resolverMensajeErrorRespuesta(err);
        console.error(
          'Error al generar enlace de firma (tras documentos):',
          err,
        );
      }
    }

    this.onProgressCompleted();
  }

  private marcarErrorGeneracion(paso: number, mensaje: string): void {
    this.actualizarPasoGeneracion(paso, 'error', mensaje);
    if (this.modalRef?.componentInstance) {
      this.modalRef.componentInstance.showCloseButton = true;
      this.modalRef.componentInstance.buttons = [
        {
          text: 'Cerrar',
          color: 'btn-primary',
          action: 'close',
          value: true,
        },
      ];
    }
  }

  private actualizarPasoGeneracion(
    index: number,
    status: 'pending' | 'in-progress' | 'completed' | 'error',
    errorMessage?: string,
  ): void {
    this.pasosGeneracionDocumentos = this.pasosGeneracionDocumentos.map(
      (step, i) => {
        if (i !== index) {
          return step;
        }
        return {
          ...step,
          status,
          ...(errorMessage ? { errorMessage } : {}),
        };
      },
    );
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private getUsuarioSesion(): any {
    const userSession = sessionStorage.getItem('user');
    return userSession ? JSON.parse(userSession) : null;
  }

  /**
   * Id de tipo de solicitud enlazado al tipo de novedad elegido (`GET tipos-novedad.idTipoSolicitud`).
   */
  private resolverIdTipoSolicitudParaTraslados(stateTipoNovedad: any): number {
    const n = (v: unknown): number => {
      const x = Number(v);
      return Number.isFinite(x) && x > 0 ? x : 0;
    };
    const fromPersisted =
      n(stateTipoNovedad?.idTipoSolicitud) ||
      n(stateTipoNovedad?.selectedTipoNovedad?.idTipoSolicitud) ||
      n(stateTipoNovedad?.tipoNovedad?.idTipoSolicitud);
    const fromParent = n(this.tipoNovedadSeleccionada?.idTipoSolicitud);
    const fromCampoInterno = n(this.idTipoSolicitudTraslado);
    const fromCaptura = n(this.idTipoSolicitudCapturadoParaDocumentos);
    return fromPersisted || fromParent || fromCampoInterno || fromCaptura;
  }

  private resolverEmailContratante(): string {
    const contrato = this.currentContract as any;
    const fromContrato = String(
      contrato?.EmailContratante ??
        contrato?.emailContratante ??
        contrato?.Email ??
        contrato?.email ??
        '',
    ).trim();
    if (fromContrato) {
      return fromContrato;
    }

    const usuario = this.getUsuarioSesion();
    const fromSesion = String(
      usuario?.email ?? usuario?.correo ?? usuario?.mail ?? '',
    ).trim();
    return fromSesion;
  }

  private buildRadicarSolicitudRequest(tipo: string): RadicarSolicitudRequest {
    const sarlaft = this.preguntasSarlaft.form.value;
    const autorizacion = this.autorizacionRadicacion.form.value;
    const usuario = this.getUsuarioSesion();
    const stateTipoNovedad = this.getTipoDeNovedadLocalStorage();

    const nombreCompleto = usuario
      ? `${usuario.nombre} ${usuario.primerApellido}`.trim()
      : '';

    const numeroDoc = usuario?.document.split('.').pop().trim();

    const idContratoOrigen = this.resolveIdContratoOrigen();

    return {
      /* ===============================
       RANGOS
       =============================== */
      rangos: sarlaft.rangos,

      /* ===============================
       ACTIVOS / PASIVOS
       =============================== */
      activos: sarlaft.activos,
      pasivos: sarlaft.pasivos,

      /* ===============================
       DESCRIPCIÓN / EMAIL
       =============================== */
      descripcionOtrasFuentes: sarlaft.descripcionOtrasFuentes,
      emailContratante: this.resolverEmailContratante(),

      /* ===============================
       DATOS SOLICITUD
       =============================== */
      codigoPrograma: this.currentContract.ProgramaCodigo.split('|')[0].trim(),
      idUsuarioCreador: numeroDoc ? Number(numeroDoc) : null,
      nombreUsuarioCreador: nombreCompleto ? nombreCompleto : 'SYSTEM',
      estadoSolicitud: tipo,
      numeroContrato: this.selectedContract,
      idRegional: this.currentContract.Regional.split('|')[0].trim(),
      idSucursal: this.currentContract.Sucursal.split('|')[0].trim(),
      idSolicitudTemporal: this.idSolicitudTemporal,

      /* ===============================
       ORIGEN FONDOS
       =============================== */
      origenFondos: {
        ahorros: sarlaft.origenFondos.ahorros,
        salarios: sarlaft.origenFondos.salarios,
        honorarios: sarlaft.origenFondos.honorarios,
        otros: sarlaft.origenFondos.otros,
        otrosDescripcion: sarlaft.origenFondos.otrosDescripcion,
        ventaActivos: sarlaft.origenFondos.ventaActivos,
        arrendamientos: sarlaft.origenFondos.arrendamientos,
        rendimientos: sarlaft.origenFondos.rendimientos,
        comisiones: sarlaft.origenFondos.comisiones,
      },

      /* ===============================
       MONEDA EXTRANJERA
       =============================== */
      monedaExtranjera: sarlaft.monedaExtranjera,

      /* ===============================
       PEP
       =============================== */
      pep: {
        esPep: sarlaft.pep.esPep,
        cargo: sarlaft.pep.cargo || '',
        fechaInicio: sarlaft.pep.fechaInicio || '',
        fechaFin: sarlaft.pep.fechaFin || '',
        representaONG: sarlaft.pep.representaONG,
        cargoONG: sarlaft.pep.cargoONG || '',
        fechaInicioONG: sarlaft.pep.fechaInicioONG || '',
        fechaFinONG: sarlaft.pep.fechaFinONG || '',
        vinculoPep: sarlaft.pep.vinculoPep,
        nombrePep: sarlaft.pep.nombrePep || '',
        cargoPep: sarlaft.pep.cargoPep || '',
        parentesco: sarlaft.pep.parentesco || '',
      },

      /* ===============================
       AUTORIZACIÓN
       =============================== */
      autorizacionCanales: autorizacion,

      /* ===============================
       SOPORTES (SIN archivo)
       =============================== */
      soportes: this.mapSoportes(),
      /* ===============================
       CONTRATO ORIGEN
       =============================== */
      contratoOrigen: {
        ...(idContratoOrigen != null
          ? {
              idContratoOrigen,
              ID_CONTRATO_ORIGEN: idContratoOrigen,
            }
          : {}),
        numeroContrato: this.currentContract.contrato,
        tipoPlan: this.currentContract.TipoPlan,
        estadoContrato: this.currentContract.EstadoContrato,
        programa: this.currentContract.ProgramaCodigo,
        cuenta:
          this.currentContract.cuenta ?? this.currentContract.Cuenta ?? '',
        corte: this.currentContract.DiaCorte.toString(),
        codigoTarifa: this.currentContract.CodigoTarifa,
        tarifaOrigen: this.currentContract.CodigoTarifa,
        tipoCuenta: this.currentContract.TipoCuenta,
        tipoSubcuenta: this.currentContract.TipoSubCuenta,
        regional: this.currentContract.Regional,
        sucursal: this.currentContract.Sucursal,
        colectivoEspecial: this.currentContract.ColectivoEspecial,
        lineaNegocio: this.currentContract.LineaNegocio,
        grupoPrograma: this.currentContract.GrupoPrograma,
        nitEmpresa: this.currentContract.Nit,
        subCuenta: stateTipoNovedad?.subcuentaDestino?.codigo ?? null,
        asesorPoblacion: this.currentContract.AsesorPoblacion,
        asesorGestion: this.currentContract.AsesorGestion,
        fechaInicio: this.currentContract.fecha_inicio || null,
        fechaVigencia: this.currentContract.fecha_vigencia || null,
        fechaBaja: this.currentContract.fecha_baja || null,
      },
      contratoDestino: stateTipoNovedad?.contratoDestino?.codigo.trim() ?? null,
      datosTipoNovedad: this.buildDatosTipoNovedad(stateTipoNovedad),
    };
  }

  private buildDatosTipoNovedad(state: any): DatosTipoNovedadRequest | null {
    if (!state || !state.selectedTipoNovedad) {
      return null;
    }

    const tipoNovedadId = Number(state.selectedTipoNovedad.codigo);

    const subtipoNovedadId = state.selectedSubtipoNovedad?.id
      ? Number(state.selectedSubtipoNovedad.id)
      : null;

    const tipo = state.selectedTipoNovedad.codigoTipoNovedad;
    const subtipo = state.selectedSubtipoNovedad?.codigoSubtipo ?? null;

    const proporcionalidad = state.proporcionalidad === 'Si' ? 1 : 0;
    const incluirAnexo = state.incluirAnexo ? 1 : 0;

    // =========================
    // BASE
    // =========================
    const base: DatosTipoNovedadRequest = {
      idTipoNovedad: tipoNovedadId,
      idSubtipoNovedad: subtipoNovedadId,
    };

    if (tipo === 'TRASLADO_CONTRATO_EXISTENTE') {
      base.contratoExistente = {
        tipoPlanDestino: state.tipoPlanDestinoExistente,
        programaDestino: state.programaDestinoExistente,
        grupoProgramaDestino: state.grupoProgramaDestinoExistente,
        tarifaDestino: state.codTarifaDestinoExistente,

        proporcionalidad,

        tipoIdentificacionContratante:
          state.tipoIdentificacionContratanteDestino?.codigo,
        numeroIdentificacion: state.numeroIdentificacionContratanteDestino,

        contratoDestino: state.contratoDestino?.codigo,
        corteContratoDestino: state.corteContratoDestino,
        lineaNegocioDestino: state.lineaNegocioDestinoExistente,

        idTipoPrima: state.tipoPrimaDestinoExistente,
        sucursalComercializableDestino: state.sucursalDestino?.nombre,
        valorTotalCuotaMesIva:
          state?.totalCuotaIva != null ? String(state.totalCuotaIva) : '',
      };
    }

    if (tipo === 'TRASLADO_CONTRATO_NUEVO' || tipo === 'CAMBIO_PLAN') {
      base.cambioPlan = {
        tipoPlanDestino: state.tipoPlanDestinoContrato?.codigo,
        programaDestino: state.programaDestinoContrato?.codigo,
        grupoProgramaDestino: state.programaDestinoContrato?.grupoPrograma,

        tarifaDestino: state.tarifaDestinoContrato?.codigo,
        subcuentaDestino: state.subcuentaDestinoContrato?.nombre,
        corteDestino: state.corteDestinoContrato,

        proporcionalidad,
        incluirAnexo,

        sucursalComercializableDestino:
          state.sucursalComercializableContrato?.nombre,
        contratoNuevo: tipo === 'TRASLADO_CONTRATO_NUEVO' ? 1 : 0,
        valorTotalCuotaMesIva:
          state?.totalCuotaIva != null ?
            String(state.totalCuotaIva) : '',
      };
    }

    if (tipo === 'CAMBIO_TARIFA') {
      base.datosCambioTarifa = {
        tarifa: state.tarifaCambio?.codigo,
        valorTotalCuotaMes: state?.valorTotalCuotaIva.toString(),
      };
    }

    if (tipo === 'CAMBIO_COBERTURA') {
      if (
        subtipo &&
        ['TRASLADO_DOWNGRADE', 'TRASLADO_UPGRADE', 'TRASLADO_LINEAL'].includes(
          subtipo,
        )
      ) {
        base.lineal = {
          tipoPlanDestino: state.tipoPlanDestino?.codigo,
          programaDestino: state.programaDestino?.codigo,
          grupoProgramaDestino:  state.programaDestino?.grupoPrograma,
          tarifaDestino: state.tarifaDestino?.codigo,

          corteDestino: state.corteDestino,

          proporcionalidad,
          incluirAnexo,

          sucursalComercializableDestino: state.sucursalDestino?.nombre,
          valorTotalCuotaMesIva:
            state?.totalCuotaIva != null ? String(state.totalCuotaIva) : '',
        };
      }

      if (
        ['TRASLADO_COLECTIVO', 'CAMBIO_PERSONA_NATURAL_A_JURIDICO'].includes(
          subtipo,
        )
      ) {
        base.colectivos = {
          tipoPlanDestino: state.tipoPlanDestino?.codigo,
          programaDestino: state.programaDestinoColectivo?.codigo,
          grupoProgramaDestino: state.programaDestinoColectivo?.grupoPrograma,
          tarifaDestino: state.tarifaDestinoColectivo?.codigo,

          subcuentaDestino: state.subcuentaDestino?.nombre,
          corteDestino: state.corteSubcuenta?.codigo,

          proporcionalidad,
          incluirAnexo,

          colectivo: state.colectivo?.codigo,
          sucursalComercializableDestino: state.sucursalDestino?.nombre,
          corteSubcuentaColectivo: state.corteSubcuenta?.codigo,
          valorTotalCuotaMesIva:
            state?.totalCuotaIva != null ? String(state.totalCuotaIva) : '',
        };
      }
    }

    if (tipo === 'CAMBIO_CONTRATANTE') {
      if (
        ['CAMBIO_RESPONSABLE_PAGADOR', 'CAMBIO_TITULARIDAD'].includes(subtipo)
      ) {
        base.cambioContratante = {
          tipoIdentificacion: state.tipoDocumentoContratante,
          nroIdentificacion: state.numeroDocumentoContratante,

          nombreContratante: [
            state.primerNombreContratante,
            state.segundoNombreContratante,
            state.primerApellidoContratante,
            state.segundoApellidoContratante,
          ]
            .filter(Boolean)
            .join(' '),
          datosCfgDebitoAutomatico: (() => {
            const rawTipo = String(state.metodoDebitoAutomatico ?? '').trim();
            const rawTipoCuenta = String(state.tipoCuentaDebito ?? '').trim();

            const tipoDebito =
              rawTipo === 'tarjeta'
                ? 'TARJETA_CREDITO'
                : rawTipo === 'cuenta'
                  ? 'CUENTA_BANCARIA'
                  : rawTipo;

            const tipoCuenta =
              rawTipoCuenta === 'AHO'
                ? 'AHORROS'
                : rawTipoCuenta === 'CTE'
                  ? 'CORRIENTE'
                  : rawTipoCuenta;

            return {
              activarDebitoAutomatico:
                state.activarDebitoAutomaticoNuevoResponsable ? 1 : 0,
              tipoDebito: tipoDebito,
              solicitoIngresoApp: state.confirmacionGestionTarjeta ? 1 : 0,
              tipoCuenta: tipoCuenta,
              codBanco: state.bancoDebito ?? '',
              nombreBanco: state.bancoDebitoNombre ?? '',
              nroCuenta: state.numeroCuentaDebito ?? '',
            } as any;
          })(),
        };
      }
    }

    return base;
  }

  private mapSoportes(): any[] {
    return this.soporteDocumentosCards.map((card) => ({
      completo: card.isCompleted(),
      documentos: card.documentosState.map((doc) => ({
        id: doc.id,
        nombre: doc.nombre,
        obligatorio: doc.obligatorio,
        tipoDocumento: doc.tipoDocumento,
        rutaArchivo: doc.rutaArchivo,
        cargado: doc.cargado,
      })),
    }));
  }

  onProgressCompleted() {
    this.modalRef.close('completed');

    const modalRefContrato = this.modalService.open(ModalBasicComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      size: 'lg',
      windowClass: 'custom-modal-radius',
    });

    modalRefContrato.componentInstance.title = 'RESUMEN DOCUMENTOS GENERADOS';
    modalRefContrato.componentInstance.content = this.contratoGenerado;
    modalRefContrato.componentInstance.buttons = [
      {
        text: 'Cerrar',
        color: 'btn-primary',
        action: 'close',
        value: true,
      },
    ];
  }

  private abrirModalInfoCuestionarioSalud(
    mensaje: string,
    onClose?: () => void,
  ): void {
    if (this.modalAbiertoCuestionarioSalud) return;
    this.modalAbiertoCuestionarioSalud = true;

    const modalRef = this.modalService.open(ModalBasicComponent, {
      centered: true,
      windowClass: 'custom-modal-radius',
    });
    modalRef.componentInstance.title = 'Información';
    modalRef.componentInstance.content = mensaje;
    modalRef.componentInstance.buttons = [
      {
        text: 'Cerrar',
        color: 'btn-primary',
        minWidth: 'none',
        action: 'close',
        value: true,
      },
    ];

    modalRef.result.finally(() => {
      this.modalAbiertoCuestionarioSalud = false;
      onClose?.();
    });
  }

  abrirModalErrorCuestionarioSalud(err: any): void {
    const state = this.localStorageService.load('frmConsultState');
    if (!state || state.activeStep !== 3) return;

    this.mostrarCuestionariosSalud = false;

    const intentar = () => {
      if (this.modalAbiertoCuestionarioSalud) {
        setTimeout(intentar, 300);
        return;
      }
      this.abrirModalInfoCuestionarioSalud(err.error?.message);
    };

    intentar();
  }

  generarIdSolicitudTemporal(): void {
    if (!this.idSolicitudTemporal) {
      this.idSolicitudTemporal = uuidv4();
      this.saveToLocalStorage(this.stepper?.selectedIndex ?? 0);
    }
  }

  consultarSoportes(): void {
    const state = this.getTipoDeNovedadLocalStorage();

    const tipoNovedadId = state?.selectedTipoNovedad?.codigo
      ? Number(state.selectedTipoNovedad.codigo)
      : null;

    const subtipoNovedadId = state?.selectedSubtipoNovedad?.id
      ? Number(state.selectedSubtipoNovedad.id)
      : null;

    const request: SoporteDocumentalRequest = {
      tipoNovedadId,
      subtipoNovedadId,
      integrantes: this.resultadosInterfazAuditoria ?? [],
    };

    this.isLoadingSoportes = true;

    this.soporteDocumentalService.consultarDocumentos(request).subscribe({
      next: (response) => {
        this.soportesDocumentales = response;
        this.isLoadingSoportes = false;
        this.saveToLocalStorage(5);
      },
      error: (err) => {
        this.isLoadingSoportes = false;
        console.error('Error al consultar soportes:', err);
      },
    });
  }

  guardarIntegrantes(snapshotsPayloads: CuestionarioSnapshotRequest[]): void {
    const integrantes: IntegranteSolicitudRequest[] =
      this.cumplePerfilamiento.map((integrante) => {
        const [tipoDocumento, nroDocumento] =
          integrante.Identificacion.split('-');
        return {
          idSolicitudTemporal: this.idSolicitudTemporal,
          tipoDocumento,
          nroDocumento,
          nombreUsuario: integrante.NombreUsuario.trim(),
          parentesco: integrante.Parentesco,
          edad: integrante.Edad,
          genero: integrante.Genero,
          fechaNacimiento: this.formatearFecha(integrante.FechaNacimiento),
          fechaAntiguedad: this.formatearFecha(integrante.FechaAntiguedad),
          anexo: integrante.Anexo || null,
          campanaVigente: integrante.CampaniaVigente || null,
          congelamiento: integrante.Congelamiento === 'NO' ? 0 : 1,
          estado: 1,
          nombreEps: null,
          regimenEps: integrante.regimen,
          telefono: this.currentContract.TelefonoFijo?.split('EXT')[0].trim(),
          celular: this.currentContract.TelefonoCelular,
          correo: this.currentContract.EmailContratante,
          direccion: null,
          departamento: null,
          municipio: null,
          nroAutoAreaCoberturaCem: null,
        };
      });

    this.integrantesService.guardarLote(integrantes).subscribe({
      next: (res) => {
        this.integrantesSolicitudGuardados = (Array.isArray(res) ? res : [])
          .map((r: any, index: number) => ({
            id: Number(r?.id ?? 0),
            nombreUsuario: String(r?.nombreUsuario ?? '').trim(),
            nroDocumento: String(
              r?.nroDocumento ?? integrantes[index]?.nroDocumento ?? '',
            ).trim(),
          }))
          .filter((r) => Number.isFinite(r.id) && r.id > 0);

        this.saveToLocalStorage(this.stepper.selectedIndex + 1);
        this.guardarCopiaCuestionarioSalud(snapshotsPayloads);
      },
      error: (err) => console.error('Error al guardar integrantes:', err),
    });
  }

  private formatearFecha(fecha: string | null): string | null {
    if (!fecha) return null;
    const [dia, mes, anio] = fecha.split('/');
    return `${anio}-${mes}-${dia}`;
  }

  getTipoDeNovedadLocalStorage() {
    const raw = localStorage.getItem('tipoNovedadSelectorState');

    return raw ? JSON.parse(raw) : null;
  }

  private actualizarTituloDetalleContrato(numero?: string): void {
    if (numero !== undefined) {
      this.numeroContratoTituloDetalle = String(numero ?? '').trim();
    }

    const title = this.numeroContratoTituloDetalle
      ? `${this.tituloDetalleContratoBase} #${this.numeroContratoTituloDetalle}`
      : this.tituloDetalleContratoBase;

    this.items = this.items.map((item, index) =>
      index === 1 ? { ...item, title } : item,
    );
  }

  trackByIndex(index: number): number {
    return index;
  }
}
